/**
 * 🎨 TipTap Inline Editor - Seamless Rich Text Editing
 * 
 * Features:
 * - Inline-Editing ohne Layout-Shift
 * - Floating Toolbar mit Font Size, Color, Bold, Italic
 * - Conditional Rendering (Edit Mode = TipTap, Live View = HTML)
 * - Komplett seamless - kein Padding-Sprung
 */

import React, { useEffect, useState, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { TextStyle } from '@tiptap/extension-text-style';
import { Color } from '@tiptap/extension-color';
import FontFamily from '@tiptap/extension-font-family';
import Underline from '@tiptap/extension-underline';
import Strike from '@tiptap/extension-strike';
import Placeholder from '@tiptap/extension-placeholder';
import Link from '@tiptap/extension-link';
import './TipTapInlineEditor.css';
import './TipTapTextAnimations.css';
import { useDebouncedCallback } from '../../hooks/useDebounce';
import logger from '../../utils/logger';

// ✅ Custom Extensions
import { Extension } from '@tiptap/core';
import { Plugin, PluginKey } from '@tiptap/pm/state';

const FontSize = Extension.create({
    name: 'fontSize',
    
    addOptions() {
        return {
            types: ['textStyle'],
        };
    },
    
    addGlobalAttributes() {
        return [
            {
                types: this.options.types,
                attributes: {
                    fontSize: {
                        default: null,
                        parseHTML: element => element.style.fontSize || null,
                        renderHTML: attributes => {
                            if (!attributes.fontSize) {
                                return {};
                            }
                            return {
                                style: `font-size: ${attributes.fontSize}`,
                            };
                        },
                    },
                },
            },
        ];
    },
    
    addCommands() {
        return {
            setFontSize: fontSize => ({ chain }) => {
                return chain()
                    .setMark('textStyle', { fontSize })
                    .run();
            },
            unsetFontSize: () => ({ chain }) => {
                return chain()
                    .setMark('textStyle', { fontSize: null })
                    .removeEmptyTextStyle()
                    .run();
            },
        };
    },
});

// ✅ Custom Extension für Font Weight auf selektierten Text
const FontWeight = Extension.create({
    name: 'fontWeight',
    
    addOptions() {
        return {
            types: ['textStyle'],
        };
    },
    
    addGlobalAttributes() {
        return [
            {
                types: this.options.types,
                attributes: {
                    fontWeight: {
                        default: null,
                        parseHTML: element => element.style.fontWeight || null,
                        renderHTML: attributes => {
                            if (!attributes.fontWeight) {
                                return {};
                            }
                            return {
                                style: `font-weight: ${attributes.fontWeight}`,
                            };
                        },
                    },
                },
            },
        ];
    },
    
    addCommands() {
        return {
            setFontWeight: fontWeight => ({ chain }) => {
                return chain()
                    .setMark('textStyle', { fontWeight })
                    .run();
            },
            unsetFontWeight: () => ({ chain }) => {
                return chain()
                    .setMark('textStyle', { fontWeight: null })
                    .removeEmptyTextStyle()
                    .run();
            },
        };
    },
});

// 🎨 Custom Extension: Mark Preservation bei "Select All + Delete"
// Löst das Problem dass Farben, FontSize, FontWeight UND FontFamily verloren gehen wenn man Ctrl+A → Delete macht
const MarkPreservation = Extension.create({
    name: 'markPreservation',
    
    addStorage() {
        return {
            preservedMarks: null,
            editorWasEmpty: false,
            lastFontFamily: null // ✅ NEW: Speichere letzte Font Family separat
        };
    },
    
    onCreate() {
        // Listen to editor transactions
        this.editor.on('transaction', ({ transaction }) => {
            const state = this.editor.state;
            const isEmpty = state.doc.textContent.trim() === '';
            
            // Speichere Marks wenn Editor NICHT leer ist
            if (!isEmpty && state.selection.$from.marks().length > 0) {
                this.storage.preservedMarks = state.selection.$from.marks();
                
                // ✅ NEW: Speichere Font Family separat (falls gesetzt)
                const fontFamilyMark = state.selection.$from.marks().find(m => m.type.name === 'textStyle' && m.attrs.fontFamily);
                if (fontFamilyMark) {
                    this.storage.lastFontFamily = fontFamilyMark.attrs.fontFamily;
                    // logger.dev('📝 [MarkPreservation] Font Family gespeichert:', this.storage.lastFontFamily);
                }
                
                // logger.dev('📝 [MarkPreservation] Marks gespeichert:', this.storage.preservedMarks);
            }
            
            // Merke dir ob Editor leer wurde
            this.storage.editorWasEmpty = isEmpty;
        });
    },
    
    addProseMirrorPlugins() {
        const storage = this.storage;
        
        // ✅ Erstelle PluginKey außerhalb für Zugriff auf State
        const pluginKey = new PluginKey('markPreservation');
        
        return [
            new Plugin({
                key: pluginKey,
                
                state: {
                    init: () => ({ lastMarks: null, lastFontFamily: null }),
                    apply: (tr, value, oldState, newState) => {
                        // Speichere Marks BEVOR sie verloren gehen
                        const oldIsEmpty = oldState.doc.textContent.trim() === '';
                        const newIsEmpty = newState.doc.textContent.trim() === '';
                        
                        // Wenn Editor gerade leer wurde → Marks sichern
                        if (!oldIsEmpty && newIsEmpty) {
                            const marks = oldState.selection.$from.marks();
                            if (marks.length > 0) {
                                // ✅ NEW: Extrahiere Font Family aus Marks
                                const fontFamilyMark = marks.find(m => m.type.name === 'textStyle' && m.attrs.fontFamily);
                                const lastFontFamily = fontFamilyMark ? fontFamilyMark.attrs.fontFamily : null;
                                
                                console.log('💾 [MarkPreservation] Editor wurde leer - Marks gesichert:', marks);
                                console.log('💾 [MarkPreservation] Font Family gesichert:', lastFontFamily);
                                
                                return { lastMarks: marks, lastFontFamily };
                            }
                        }
                        
                        return value;
                    }
                },
                
                appendTransaction: (transactions, oldState, newState) => {
                    // ✅ Hole Plugin-State über PluginKey
                    const pluginState = pluginKey.getState(newState);
                    const isEmpty = newState.doc.textContent.trim() === '';
                    
                    // Wenn Editor leer ist und wir gespeicherte Marks haben
                    if (isEmpty && pluginState && pluginState.lastMarks && pluginState.lastMarks.length > 0) {
                        // Prüfe ob storedMarks fehlen
                        const currentStoredMarks = newState.storedMarks;
                        
                        if (!currentStoredMarks || currentStoredMarks.length === 0) {
                            console.log('🔧 [MarkPreservation] Restoring storedMarks:', pluginState.lastMarks);
                            if (pluginState.lastFontFamily) {
                                console.log('🔧 [MarkPreservation] inklusive Font Family:', pluginState.lastFontFamily);
                            }
                            
                            const tr = newState.tr;
                            tr.setStoredMarks(pluginState.lastMarks);
                            return tr;
                        }
                    }
                    
                    return null;
                },
                
                // ✅ WICHTIG: Bei jedem Tastendruck die Marks anwenden
                props: {
                    handleKeyDown: (view, event) => {
                        // Nur bei echten Text-Eingaben (keine Modifier-Keys)
                        if (event.ctrlKey || event.metaKey || event.altKey) return false;
                        
                        // Nur bei Zeichen-Eingabe (nicht bei Pfeiltasten, etc.)
                        if (event.key.length !== 1) return false;
                        
                        const isEmpty = view.state.doc.textContent.trim() === '';
                        // ✅ Hole Plugin-State über PluginKey
                        const pluginState = pluginKey.getState(view.state);
                        
                        if (isEmpty && pluginState && pluginState.lastMarks && pluginState.lastMarks.length > 0) {
                            console.log('⌨️ [MarkPreservation] Taste gedrückt in leerem Editor - Marks werden angewendet');
                            if (pluginState.lastFontFamily) {
                                console.log('⌨️ [MarkPreservation] inklusive Font Family:', pluginState.lastFontFamily);
                            }
                            
                            // Setze storedMarks BEVOR der Buchstabe eingefügt wird
                            const tr = view.state.tr;
                            tr.setStoredMarks(pluginState.lastMarks);
                            view.dispatch(tr);
                        }
                        
                        return false; // Nutze default handling
                    }
                }
            })
        ];
    }
});

function TipTapInlineEditor({
    value,
    onChange,
    isEditMode,
    placeholder = 'Click to edit...',
    element = 'div',
    style = {},
    className = '',
    onSelect,
    enableRichText = true,
    showFloatingToolbar = true,
    colors = {}, // ✅ Brand Colors
    onDeselect, // ✅ Deselect Callback
    onStyleChange, // ✅ Style Changes (Margin, Padding, etc.) - Desktop
    currentMargins = { top: 0, right: 0, bottom: 0, left: 0 }, // ✅ Current Margin Values (Desktop)
    onStyleChangeMobile, // ✅ Style Changes Mobile
    currentMarginsMobile = { top: 0, right: 0, bottom: 0, left: 0 }, // ✅ Current Margin Values (Mobile)
    onFontSizeChange, // ✅ Font Size Change Callback (Desktop)
    currentFontSize = 16, // ✅ Current Font Size (Desktop)
    onFontSizeMobileChange, // ✅ Font Size Change Callback (Mobile)
    currentFontSizeMobile = 14, // ✅ Current Font Size (Mobile)
    onFontWeightChange, // ✅ Font Weight Change Callback (Desktop)
    currentFontWeight = 400, // ✅ Current Font Weight (Desktop)
    onFontWeightMobileChange, // ✅ Font Weight Change Callback (Mobile)
    currentFontWeightMobile = 400, // ✅ Current Font Weight (Mobile)
    onMaxWidthChange, // ✅ Max Width Change Callback (Desktop)
    currentMaxWidth = 800, // ✅ Current Max Width (Desktop)
    onMaxWidthMobileChange, // ✅ Max Width Change Callback (Mobile)
    currentMaxWidthMobile = 600, // ✅ Current Max Width (Mobile)
    isMobileLayout = false, // ✅ Mobile Layout State (für responsive CSS)
    availableFonts = [], // ✅ Available Font Families (from FontContext)
    currentFontFamily = '', // ✅ Current Font Family
    onFontFamilyChange, // ✅ Font Family Change Callback
    brandFonts = {}, // ✅ NEW: Brand Fonts (globalBigFont, globalSmallFont)
    // ✅ Line Height Controls
    onLineHeightChange, // ✅ Line Height Change Callback (Desktop)
    currentLineHeight = 1.5, // ✅ Current Line Height (Desktop)
    onLineHeightMobileChange, // ✅ Line Height Change Callback (Mobile)
    currentLineHeightMobile = 1.5, // ✅ Current Line Height (Mobile)
    // ✅ Letter Spacing Controls
    onLetterSpacingChange, // ✅ Letter Spacing Change Callback (Desktop)
    currentLetterSpacing = 0, // ✅ Current Letter Spacing (Desktop)
    onLetterSpacingMobileChange, // ✅ Letter Spacing Change Callback (Mobile)
    currentLetterSpacingMobile = 0, // ✅ Current Letter Spacing (Mobile)
    // ✅ Text Alignment Controls
    onTextAlignChange, // ✅ Text Alignment Change Callback (Desktop)
    currentTextAlign = 'left', // ✅ Current Text Alignment (Desktop)
    onTextAlignMobileChange, // ✅ Text Alignment Change Callback (Mobile)
    currentTextAlignMobile = 'left', // ✅ Current Text Alignment (Mobile)
    // ✅ Text Box Controls (Badge/Tag Style)
    onTextBoxChange, // ✅ Text Box Change Callback
    textBoxEnabled = false, // ✅ Text Box Toggle
    textBoxPaddingHorizontal = 12, // ✅ Padding left/right (breiter)
    textBoxPaddingVertical = 6, // ✅ Padding top/bottom (höher)
    textBoxBackgroundColor = '#000000', // ✅ Background Color (Schwarz als Default)
    textBoxBorderEnabled = false, // ✅ Border Toggle
    textBoxBorderWidth = 2, // ✅ Border Width (2px als Default)
    textBoxBorderColor = '#ffffff', // ✅ Border Color (Weiß als Default)
    textBoxBorderRadius = 8, // ✅ Border Radius (8px als Default)
    // ✅ Animation Controls
    onAnimationChange, // ✅ Animation Change Callback
    animationEnabled = false, // ✅ Animation Toggle
    animationType = 'fadeInUp', // ✅ Animation Type (fadeInUp, slideInLeft, fadeIn, elegantScale)
    animationSpeed = 'normal', // ✅ Animation Speed (fast, normal, slow)
    triggerAnimation = null // ✅ Trigger Animation (timestamp or counter)
}) {
    // 🎨 Floating Toolbar State
    const [showToolbar, setShowToolbar] = useState(false);
    const [toolbarPosition, setToolbarPosition] = useState({ top: 0, left: 0 });
    const toolbarRef = useRef(null);
    const wrapperRef = useRef(null);
    const liveViewRef = useRef(null); // ✅ WICHTIG: Ref für Live View (muss auf Top-Level sein!)
    
    // 🎯 Dragging State
    const [isDragging, setIsDragging] = useState(false);
    const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
    const [isManuallyPositioned, setIsManuallyPositioned] = useState(false);
    
    // 🎯 Unique Editor ID für globale Event-Kommunikation
    const editorIdRef = useRef(`tiptap-editor-${Math.random().toString(36).substr(2, 9)}`);
    
    // 🎯 Expandable Sections State
    const [expandedSection, setExpandedSection] = useState(null); // 'colors', 'margins', 'fontSize', 'textFontFamily', 'textFontSize', 'textFontWeight', 'typography', 'link', 'effects'
    const [savedSelection, setSavedSelection] = useState(null); // Save selection für Color Application
    const [originalHTML, setOriginalHTML] = useState(null); // ✅ Original HTML (wird nicht mehr für Preview benutzt, aber andere Code-Teile brauchen es noch)
    const [isPreviewingFont, setIsPreviewingFont] = useState(false); // ✅ Nicht mehr aktiv genutzt, aber andere Code-Teile brauchen es
    const [linkUrl, setLinkUrl] = useState(''); // ✅ Link URL für Link-Editor
    
    // 🎬 Animation State - Trigger Animation when section is opened
    const [animationKey, setAnimationKey] = useState(0);
    const prevTriggerRef = useRef(triggerAnimation);
    const [hasPlayedAnimation, setHasPlayedAnimation] = useState(false); // ✅ Track if animation already played
    const observerRef = useRef(null); // ✅ Intersection Observer

    // ⚡ REMOVED: Optimistic UI State - Wird jetzt im PARENT gehandhabt!
    // TipTap ist zu komplex für internen optimistic state wegen DOM-Manipulationen
    
    // 🎨 CUSTOM COLOR SLOTS - 5 fixe Slots, persistent über LocalStorage
    const [customColorSlots, setCustomColorSlots] = useState(() => {
        try {
            const stored = localStorage.getItem('tiptap-custom-color-slots');
            return stored ? JSON.parse(stored) : ['#ffffff', '#ffffff', '#ffffff', '#ffffff', '#ffffff'];
        } catch (error) {
            console.error('Failed to load custom color slots from localStorage:', error);
            return ['#ffffff', '#ffffff', '#ffffff', '#ffffff', '#ffffff'];
        }
    });
    
    // 🎨 Aktuell editierter Slot (für Color Picker)
    const [editingSlotIndex, setEditingSlotIndex] = useState(null);

    // ❌ ENTFERNT: hasBeenFocused State - Auto-Select wurde deaktiviert
    
    // 🎯 State für Desktop/Mobile Toggle in Typography Section
    // Synchronisiert mit isMobileLayout (wird automatisch durch View-Mode-Toggle gesetzt)
    const [typographyDevice, setTypographyDevice] = useState(isMobileLayout ? 'mobile' : 'desktop');
    
    // 🎯 Synchronisiere Typography Toggle mit isMobileLayout
    useEffect(() => {
        setTypographyDevice(isMobileLayout ? 'mobile' : 'desktop');
    }, [isMobileLayout]);
    
    // 🎬 Helper Function: Trigger Animation
    const triggerAnimationEffect = () => {
        if (!wrapperRef.current) return;
        
        const element = wrapperRef.current;
        
        // Remove animation classes
        element.classList.remove('tiptap-text-animated');
        element.classList.remove(`tiptap-animation-${animationType}`);
        element.classList.remove(`tiptap-animation-speed-${animationSpeed}`);
        
        // Force reflow to restart animation
        void element.offsetWidth;
        
        // Re-add animation classes
        element.classList.add('tiptap-text-animated');
        element.classList.add(`tiptap-animation-${animationType}`);
        element.classList.add(`tiptap-animation-speed-${animationSpeed}`);
    };
    
    // 🎬 Trigger Animation when triggerAnimation prop changes (Manual Preview)
    useEffect(() => {
        if (animationEnabled && triggerAnimation !== prevTriggerRef.current) {
            triggerAnimationEffect();
            prevTriggerRef.current = triggerAnimation;
            logger.dev('🎬 Animation triggered (manual):', animationType);
        }
    }, [triggerAnimation, animationEnabled, animationType, animationSpeed]);
    
    // 🎬 Trigger Animation Preview when Effects Section is opened
    useEffect(() => {
        if (expandedSection === 'effects' && animationEnabled) {
            triggerAnimationEffect();
            logger.dev('🎬 Animation preview triggered (effects section opened)');
        }
    }, [expandedSection, animationEnabled]);
    
    // 🎬 Intersection Observer - Trigger Animation when Element enters Viewport
    useEffect(() => {
        if (!wrapperRef.current || !animationEnabled || isEditMode) return;
        
        // ✅ NUR im Live Mode (nicht im Edit Mode!)
        const element = wrapperRef.current;
        
        // ✅ Cleanup existing observer
        if (observerRef.current) {
            observerRef.current.disconnect();
        }
        
        // 🎯 INITIAL CHECK: Ist Element bereits beim Laden sichtbar?
        // Wichtig für Elemente die "above the fold" sind (direkt sichtbar beim Laden)
        const checkIfInitiallyVisible = () => {
            const rect = element.getBoundingClientRect();
            const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
            
            // ✅ IMPROVED: Element ist sichtbar wenn IRGENDEIN Teil im Viewport ist
            const isVisible = (
                rect.top < viewportHeight && // Top ist über dem Viewport-Bottom
                rect.bottom > 0 && // Bottom ist unter dem Viewport-Top
                rect.left < window.innerWidth && // Links ist vor dem Viewport-Right
                rect.right > 0 // Rechts ist nach dem Viewport-Left
            );
            
            console.log('🎬 [TipTap Animation] Initial visibility check:', {
                isVisible,
                rectTop: rect.top,
                rectBottom: rect.bottom,
                viewportHeight,
                animationEnabled,
                animationType,
                hasPlayedAnimation
            });
            
            if (isVisible && !hasPlayedAnimation) {
                console.log('✅ [TipTap Animation] Element initially visible - triggering animation!');
                // ✅ Kleine Verzögerung für smooth page load
                setTimeout(() => {
                    triggerAnimationEffect();
                    setHasPlayedAnimation(true);
                }, 100);
                return true;
            }
            return false;
        };
        
        // ✅ Check if already visible
        const wasInitiallyVisible = checkIfInitiallyVisible();
        
        // ✅ Nur Observer erstellen wenn Element NICHT bereits sichtbar war
        if (!wasInitiallyVisible) {
            console.log('🎬 [TipTap Animation] Creating Intersection Observer (element not initially visible)');
            
            // ✅ Create Intersection Observer
            observerRef.current = new IntersectionObserver(
                (entries) => {
                    entries.forEach((entry) => {
                        console.log('🎬 [TipTap Animation] Observer callback:', {
                            isIntersecting: entry.isIntersecting,
                            intersectionRatio: entry.intersectionRatio,
                            hasPlayedAnimation
                        });
                        
                        // ✅ Wenn Element ins Viewport kommt UND noch nicht abgespielt
                        if (entry.isIntersecting && !hasPlayedAnimation) {
                            console.log('✅ [TipTap Animation] Element scrolled into viewport - triggering animation!');
                            triggerAnimationEffect();
                            setHasPlayedAnimation(true); // ✅ Nur einmal abspielen
                        }
                    });
                },
                {
                    threshold: 0.1, // ✅ Trigger wenn 10% des Elements sichtbar ist (früher als vorher!)
                    rootMargin: '0px 0px 100px 0px' // ✅ Trigger 100px BEVOR Element ins Viewport kommt
                }
            );
            
            // ✅ Start observing
            observerRef.current.observe(element);
            console.log('✅ [TipTap Animation] Observer started successfully');
        }
        
        // ✅ Cleanup
        return () => {
            if (observerRef.current) {
                observerRef.current.disconnect();
            }
        };
    }, [animationEnabled, isEditMode, hasPlayedAnimation, animationType, animationSpeed]);
    
    // 🎬 Reset hasPlayedAnimation wenn Animation deaktiviert oder Type/Speed ändert
    useEffect(() => {
        if (!animationEnabled) {
            setHasPlayedAnimation(false);
        }
    }, [animationEnabled, animationType, animationSpeed]);
    
    // 🎯 Drag Handlers - MUSS VOR early return stehen!
    const handleMouseDown = (e) => {
        e.preventDefault();
        e.stopPropagation();
        
        setIsDragging(true);
        setDragOffset({
            x: e.clientX - toolbarPosition.left,
            y: e.clientY - toolbarPosition.top
        });
    };
    
    const handleMouseMove = (e) => {
        if (!isDragging) return;
        
        const newLeft = e.clientX - dragOffset.x;
        const newTop = e.clientY - dragOffset.y;
        
        // Bounds checking - Toolbar bleibt im Viewport
        const maxLeft = window.innerWidth - (toolbarRef.current?.offsetWidth || 300);
        const maxTop = window.innerHeight - (toolbarRef.current?.offsetHeight || 200);
        
        setToolbarPosition({
            left: Math.max(0, Math.min(newLeft, maxLeft)),
            top: Math.max(0, Math.min(newTop, maxTop))
        });
    };
    
    const handleMouseUp = () => {
        setIsDragging(false);
        setIsManuallyPositioned(true); // ✅ Mark as manually positioned
    };
    
    // ✅ Drag Event Listeners - MUSS VOR early return stehen!
    useEffect(() => {
        if (isDragging) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
            
            return () => {
                document.removeEventListener('mousemove', handleMouseMove);
                document.removeEventListener('mouseup', handleMouseUp);
            };
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isDragging]);
    
    // 🎨 Custom Color Slots: LocalStorage Sync
    useEffect(() => {
        try {
            localStorage.setItem('tiptap-custom-color-slots', JSON.stringify(customColorSlots));
            // logger.dev('💾 Custom color slots saved:', customColorSlots);
        } catch (error) {
            logger.error('Failed to save custom color slots to localStorage:', error);
        }
    }, [customColorSlots]);
    
    // 🚀 PERFORMANCE: Stabiler onChange-Ref verhindert unnötige debounced callback Neuerstell
    const onChangeRef = useRef(onChange);
    useEffect(() => {
        onChangeRef.current = onChange;
    }, [onChange]);
    
    // 🔄 Track last saved value to prevent race conditions
    const lastSavedValueRef = useRef(value);
    
    // 🚀 DEBOUNCED onChange - Verhindert dass jeder Tastendruck sofort in DB schreibt
    // 150ms Delay für Text-Änderungen (reduziert von 300ms für schnelleres Speichern)
    const debouncedOnChange = useDebouncedCallback((html) => {
        logger.dev('💾 [DEBOUNCED] Saving text change to DB after delay...');
        onChangeRef.current(html);
    }, 150); // ✅ Kürzerer Delay = Text wird schneller gespeichert
    
    // ✅ Ref für debouncedOnChange damit onBlur immer auf aktuelle Version zugreifen kann
    const debouncedOnChangeRef = useRef(debouncedOnChange);
    useEffect(() => {
        debouncedOnChangeRef.current = debouncedOnChange;
    }, [debouncedOnChange]);
    
    // 🧹 CLEANUP: Cancel debounced callback beim Unmount & flush pending changes
    useEffect(() => {
        return () => {
            // ✅ FLUSH pending changes beim Unmount (wichtig!)
            if (debouncedOnChange.flush) {
                debouncedOnChange.flush();
            }
            if (debouncedOnChange.cancel) {
                debouncedOnChange.cancel();
            }
        };
    }, [debouncedOnChange]);

    // ⚡ REMOVED: Debounced style callbacks - Wird jetzt im PARENT gehandhabt!
    // TipTap ruft die Callbacks direkt auf, Parent macht das Debouncing
    
    // 🎨 Sync Custom Color Slots beim Öffnen der Color Section
    useEffect(() => {
        if (expandedSection === 'colors') {
            // Lade aktuelle Slots aus LocalStorage
            try {
                const stored = localStorage.getItem('tiptap-custom-color-slots');
                if (stored) {
                    const storedSlots = JSON.parse(stored);
                    // Nur updaten wenn sich was geändert hat
                    if (JSON.stringify(storedSlots) !== JSON.stringify(customColorSlots)) {
                        setCustomColorSlots(storedSlots);
                        logger.dev('🔄 Custom color slots synchronized from LocalStorage:', storedSlots);
                    }
                }
            } catch (error) {
                console.error('Failed to sync custom color slots from localStorage:', error);
            }
        }
    }, [expandedSection]); // Trigger beim Öffnen der Section
    
    // 🎨 Update specific color slot
    const updateColorSlot = (index, color) => {
        const newSlots = [...customColorSlots];
        newSlots[index] = color;
        setCustomColorSlots(newSlots);
        logger.dev(`✅ Color slot ${index} updated to ${color}`);
    };

    // 🎯 TipTap Editor initialisieren (nur im Edit Mode)
    const editor = useEditor({
        extensions: [
            StarterKit.configure({
                heading: false,
                blockquote: false,
                codeBlock: false,
                horizontalRule: false,
                listItem: false,
                orderedList: false,
                bulletList: false,
                // ✅ PERFORMANCE FIX: Deaktiviere built-in Extensions die wir custom konfigurieren
                link: false,      // Wir nutzen custom Link.configure()
                underline: false, // Wir nutzen custom Underline Extension
                strike: false     // Wir nutzen custom Strike Extension
            }),
            TextStyle,
            Color,
            FontFamily, // ✅ Font Family für Text-Passagen
            FontSize, // ✅ Font Size für selektierten Text
            FontWeight, // ✅ Font Weight für selektierten Text
            Underline, // ✅ Custom Underline (kein Duplikat mehr)
            Strike, // ✅ Strikethrough für durchgestrichenen Text
            Link.configure({
                openOnClick: false, // ✅ Im Edit Mode nicht öffnen
                autolink: false, // ✅ DISABLED: Keine automatische Link-Erkennung (nur manuell über Toolbar)
                HTMLAttributes: {
                    target: '_blank', // ✅ Öffnet in neuem Tab
                    rel: 'noopener noreferrer', // ✅ Sicherheit
                },
            }), // ✅ Custom Link (kein Duplikat mehr)
            Placeholder.configure({
                placeholder: placeholder,
                emptyEditorClass: 'tiptap-empty'
            }),
            MarkPreservation // ✅ Preserviert Marks bei Ctrl+A → Delete
        ],
        content: value || '',
        editable: isEditMode,
        onUpdate: ({ editor }) => {
            const html = editor.getHTML();
            
            // Nur updaten wenn wirklich was geändert wurde
            if (html !== value) {
                // ✅ Track last saved value to prevent race conditions
                lastSavedValueRef.current = html;
                // ✅ DEBOUNCED: Wartet 150ms nach letztem Tastendruck bevor DB-Update
                debouncedOnChange(html);
            }
        },
        onFocus: ({ editor }) => {
            // 🎯 GLOBALES EVENT: Informiere alle anderen Editoren, dass dieser Editor fokussiert wurde
            const event = new CustomEvent('tiptap-editor-focus', {
                detail: { editorId: editorIdRef.current }
            });
            window.dispatchEvent(event);
            
            if (onSelect) onSelect();
            
            // ❌ DEAKTIVIERT: Auto-Select verursacht Datenverlust
            // User tippt → gesamter Text wird überschrieben
            // if (!hasBeenFocused && value && value.trim() !== '') {
            //     editor.commands.selectAll();
            //     setHasBeenFocused(true);
            //     logger.dev('✨ Auto-selected all text on first focus');
            // }
        },
        onBlur: ({ editor }) => {
            // ✅ FLUSH pending changes sofort (wichtig für Copy-Paste!)
            // Wenn User Text einfügt und sofort wegklickt, soll der Text trotzdem gespeichert werden
            if (debouncedOnChangeRef.current && debouncedOnChangeRef.current.flush) {
                debouncedOnChangeRef.current.flush();
                logger.dev('💾 [FLUSH] Forced save on blur (e.g. after paste)');
            }
            
            // ❌ ENTFERNT: hasBeenFocused Reset - Auto-Select wurde deaktiviert
        },
        editorProps: {
            attributes: {
                class: `tiptap-editor ${className}`,
                // ✅ FIX: Default Font Family direkt auf Editor setzen
                // Verhindert Arial als Fallback wenn Text gelöscht wird
                style: currentFontFamily ? `font-family: ${currentFontFamily}` : ''
            }
        }
    }, [isEditMode]); // 🚨 CRITICAL: Nur isEditMode als Dependency! currentFontFamily würde Editor komplett neu erstellen

    // Update editor attributes wenn sich currentFontFamily ändert (ohne Editor neu zu erstellen)
    useEffect(() => {
        if (!editor) return;
        
        // ✅ ROBUST FIX: Try-catch + double check für editor.view.dom
        try {
            // Prüfe ob editor.view und editor.view.dom existieren
            if (!editor.view || !editor.view.dom) {
                // logger.dev('⚠️ [TipTap] Editor view not yet mounted, skipping font family update');
                return;
            }
            
            const editorElement = editor.view.dom;
            if (editorElement && currentFontFamily) {
                editorElement.style.fontFamily = currentFontFamily;
                // logger.dev('🔄 [TipTap] Updated font family on editor DOM:', currentFontFamily);
            }
        } catch (error) {
            // Silently catch editor view not available errors
            if (error.message && error.message.includes('editor view is not available')) {
                // logger.dev('⚠️ [TipTap] Editor view not available yet, will retry on next render');
                return;
            }
            // Re-throw other errors
            console.error('[TipTap] Unexpected error in font family update:', error);
        }
    }, [currentFontFamily, editor]);
    
    // Update content wenn sich value ändert (von außen)
    useEffect(() => {
        if (!editor) return;
        
        try {
            // ✅ FIX: Prüfe ob editor vollständig initialisiert ist
            if (!editor.view || !editor.isEditable) {
                // logger.dev('⚠️ [TipTap] Editor not fully initialized, skipping value update');
                return;
            }
            
            // 🚨 CRITICAL FIX: Nicht updaten wenn User gerade tippt!
            // Verhindert Race Condition: User tippt → debounced onChange → value prop ändert sich → setContent überschreibt User-Input
            if (editor.isFocused) {
                // logger.dev('⚠️ [TipTap] Skipping external value update - editor is focused (user is typing)');
                return;
            }
            
            // ✅ Nur updaten wenn der value sich wirklich von außen geändert hat
            // Nicht updaten wenn es unser eigenes Update war
            if (value === lastSavedValueRef.current) {
                return;
            }
            
            const currentHTML = editor.getHTML();
            const newValue = value || '<p><br></p>'; // ✅ Default zu einem leeren Paragraph
            
            // Nur setzen wenn wirklich unterschiedlich
            // Verhindert unnötige Re-Renders die zu Verdopplung führen könnten
            if (currentHTML !== newValue) {
                logger.dev('🔄 [TipTap] setContent (external update):', { from: currentHTML.substring(0, 50), to: newValue.substring(0, 50) });
                editor.commands.setContent(newValue, false); // false = emitUpdate: false
                lastSavedValueRef.current = newValue;
            }
        } catch (error) {
            // Silently catch editor view not available errors
            if (error.message && error.message.includes('editor view is not available')) {
                // logger.dev('⚠️ [TipTap] Editor view not available yet during value update');
                return;
            }
            console.error('[TipTap] Unexpected error in value update:', error);
        }
    }, [value, editor]);

    // 🔗 Update linkUrl wenn Link-Section öffnet
    useEffect(() => {
        if (expandedSection === 'link' && editor) {
            try {
                if (!editor.view) {
                    return;
                }
                const currentLink = editor.getAttributes('link').href || '';
                setLinkUrl(currentLink);
            } catch (error) {
                // Silently catch editor errors
                if (error.message && error.message.includes('editor view is not available')) {
                    return;
                }
                console.error('[TipTap] Unexpected error in link update:', error);
            }
        }
    }, [expandedSection, editor]);

    // ✅ Initial Mount: Setze maxWidth mit !important beim ersten Render
    useEffect(() => {
        if (wrapperRef.current && (onMaxWidthChange || onMaxWidthMobileChange)) {
            const activeMaxWidth = isMobileLayout 
                ? (currentMaxWidthMobile || currentMaxWidth) 
                : currentMaxWidth;
            
            wrapperRef.current.style.setProperty('max-width', `${activeMaxWidth}px`, 'important');
        }
    }, []); // ✅ Nur beim Mount

    // ✅ Update DOM Styles sofort wenn sich Werte ändern (für Live Preview)
    useEffect(() => {
        if (wrapperRef.current) {
            // Font Size - Setze CSS Custom Properties (Props werden bereits optimistic vom Parent übergeben!)
            if (currentFontSize) {
                wrapperRef.current.style.setProperty('--normal-size', `${currentFontSize}px`);
            }
            
            if (currentFontSizeMobile) {
                wrapperRef.current.style.setProperty('--mobile-size', `${currentFontSizeMobile}px`);
            }
            
            // ✅ WICHTIG: Setze fontSize basierend auf isMobileLayout für sofortiges Feedback
            // Die CSS-Klassen (.mobile-layout) funktionieren erst beim Re-Render
            // Daher setzen wir fontSize direkt für instant Preview
            const activeFontSize = isMobileLayout 
                ? (currentFontSizeMobile || currentFontSize) 
                : currentFontSize;
            
            if (activeFontSize) {
                wrapperRef.current.style.fontSize = `${activeFontSize}px`;
            }
            
            // ✅ Max Width - Setze für Live Preview mit !important
            const activeMaxWidth = isMobileLayout 
                ? (currentMaxWidthMobile || currentMaxWidth) 
                : currentMaxWidth;
            
            if (activeMaxWidth && (onMaxWidthChange || onMaxWidthMobileChange)) {
                wrapperRef.current.style.setProperty('max-width', `${activeMaxWidth}px`, 'important');
            }
            
            // ✅ Line Height - Responsive basierend auf isMobileLayout
            const activeLineHeight = isMobileLayout 
                ? (currentLineHeightMobile || currentLineHeight) 
                : currentLineHeight;
            
            if (activeLineHeight) {
                wrapperRef.current.style.lineHeight = activeLineHeight;
            }
            
            // ✅ Letter Spacing - Responsive basierend auf isMobileLayout
            const activeLetterSpacing = isMobileLayout 
                ? (currentLetterSpacingMobile || currentLetterSpacing) 
                : currentLetterSpacing;
            
            if (activeLetterSpacing !== undefined) {
                wrapperRef.current.style.letterSpacing = `${activeLetterSpacing}px`;
            }
            
            // ✅ Text Alignment - Responsive basierend auf isMobileLayout
            const activeTextAlign = isMobileLayout 
                ? (currentTextAlignMobile || currentTextAlign) 
                : currentTextAlign;
            
            if (activeTextAlign) {
                wrapperRef.current.style.textAlign = activeTextAlign;
            }
            
            // ✅ Margins - Responsive basierend auf isMobileLayout
            // ✅ KRITISCH: Margins IMMER anwenden (auch im Live Mode!)
            const activeMargins = isMobileLayout 
                ? (currentMarginsMobile || currentMargins) 
                : currentMargins;
            
            if (activeMargins) {
                wrapperRef.current.style.marginTop = `${activeMargins.top || 0}px`;
                wrapperRef.current.style.marginRight = `${activeMargins.right || 0}px`;
                wrapperRef.current.style.marginBottom = `${activeMargins.bottom || 0}px`;
                wrapperRef.current.style.marginLeft = `${activeMargins.left || 0}px`;
            }
            
            // 🎨 COLOR PRESERVATION: Setze die Farbe direkt auf .ProseMirror Element
            // Das stellt sicher dass auch leerer/neuer Text die richtige Farbe hat
            const proseMirrorElement = wrapperRef.current.querySelector('.ProseMirror');
            if (proseMirrorElement && style.color && style.color !== 'inherit') {
                proseMirrorElement.style.color = style.color;
                // console.log('🎨 [DOM Style] Setting ProseMirror color directly:', style.color);
            }
            
            // 🎯 TEXT BOX STYLING (Badge/Tag Style)
            if (textBoxEnabled) {
                wrapperRef.current.style.paddingTop = `${textBoxPaddingVertical || 6}px`;
                wrapperRef.current.style.paddingBottom = `${textBoxPaddingVertical || 6}px`;
                wrapperRef.current.style.paddingLeft = `${textBoxPaddingHorizontal || 12}px`;
                wrapperRef.current.style.paddingRight = `${textBoxPaddingHorizontal || 12}px`;
                wrapperRef.current.style.backgroundColor = textBoxBackgroundColor || 'transparent';
                wrapperRef.current.style.borderRadius = `${textBoxBorderRadius || 8}px`;
                wrapperRef.current.style.display = 'inline-block'; // ✅ WICHTIG: Damit Padding/Border nur um Text herum ist
                wrapperRef.current.style.border = textBoxBorderEnabled 
                    ? `${textBoxBorderWidth || 2}px solid ${textBoxBorderColor || '#ffffff'}`
                    : 'none';
                wrapperRef.current.style.width = 'auto'; // ✅ Auto width für inline-block
            } else {
                // ✅ Reset Text Box Styles wenn disabled
                wrapperRef.current.style.padding = '0';
                wrapperRef.current.style.backgroundColor = 'transparent';
                wrapperRef.current.style.borderRadius = '0';
                wrapperRef.current.style.display = 'block';
                wrapperRef.current.style.border = 'none';
                wrapperRef.current.style.width = ''; // Reset to default
            }
        }
    }, [currentMargins, currentMarginsMobile, currentFontSize, currentFontSizeMobile, currentMaxWidth, currentMaxWidthMobile, currentLineHeight, currentLineHeightMobile, currentLetterSpacing, currentLetterSpacingMobile, currentTextAlign, currentTextAlignMobile, textBoxEnabled, textBoxPaddingHorizontal, textBoxPaddingVertical, textBoxBackgroundColor, textBoxBorderEnabled, textBoxBorderWidth, textBoxBorderColor, textBoxBorderRadius, isMobileLayout, onStyleChange, onStyleChangeMobile, style.color, editor]);

    // 🎨 Floating Toolbar Position Handler
    useEffect(() => {
        if (!editor || !showFloatingToolbar || !enableRichText) return;

        const updateToolbarVisibility = () => {
            const { from, to } = editor.state.selection;
            const hasSelection = from !== to;

            // ✅ FIX: Während Font Preview KEINE Toolbar-Updates
            if (isPreviewingFont) {
                return;
            }

            // ✅ FIX: Wenn interaktive Sections offen sind, Toolbar NIEMALS schließen
            // (auch wenn Selection kurzzeitig verloren geht beim Klicken auf Buttons/Inputs)
            const protectedSections = ['textFontFamily', 'effects', 'colors', 'link'];
            if (protectedSections.includes(expandedSection)) {
                setShowToolbar(true);
                return;
            }

            if (!hasSelection) {
                setShowToolbar(false);
                setExpandedSection(null); // ✅ Reset expanded section when toolbar closes
                return;
            }

            // ✅ FREEZE POSITION: Wenn expandedSection aktiv ist ODER manuell positioniert, Position nicht mehr updaten
            // Das verhindert Wackeln bei Font-Preview und respektiert User's Drag Position
            if (expandedSection || isManuallyPositioned) {
                setShowToolbar(true);
                return;
            }

            // 🎯 NEUE STRATEGIE: Fixiere Toolbar an fester Position im Viewport
            // Position: fixed → immer 80px von oben, horizontal zentriert
            
            // ✅ EINFACH: Feste Position im Viewport
            const top = 80; // Immer 80px vom Viewport-Top
            const left = window.innerWidth / 2; // Horizontal zentriert
            
            setToolbarPosition({ top, left });
            setShowToolbar(true);
        };

        // Listen to selection changes
        editor.on('selectionUpdate', updateToolbarVisibility);
        editor.on('update', updateToolbarVisibility);

        // ✅ Position nur bei Resize aktualisieren (für Zentrierung) - NICHT wenn manuell positioniert
        const handleResize = () => {
            if (showToolbar && !expandedSection && !isManuallyPositioned) {
                updateToolbarVisibility();
            }
        };

        // ✅ Nur Resize Listener (Position ist fix im Viewport, nur Zentrierung bei Resize anpassen)
        window.addEventListener('resize', handleResize);

        return () => {
            editor.off('selectionUpdate', updateToolbarVisibility);
            editor.off('update', updateToolbarVisibility);
            window.removeEventListener('resize', handleResize);
        };
    }, [editor, showFloatingToolbar, enableRichText, showToolbar, expandedSection, isPreviewingFont, isManuallyPositioned]);

    // 🎯 GLOBALER EVENT LISTENER - Schließe Toolbar komplett wenn anderer Editor/Button fokussiert wird
    useEffect(() => {
        if (!isEditMode || !editor) return;

        const handleOtherEditorFocus = (event) => {
            const focusedEditorId = event.detail?.editorId;
            
            // Wenn ein anderer Editor/Button fokussiert wurde → Schließe KOMPLETT
            if (focusedEditorId && focusedEditorId !== editorIdRef.current) {
                console.log('🔒 Other editor/button focused, closing this toolbar completely:', {
                    ownId: editorIdRef.current,
                    focusedId: focusedEditorId
                });
                
                // ✅ KOMPLETT SCHLIESSEN: Blur Editor → Selection weg → Toolbar weg
                editor.commands.blur();
                setShowToolbar(false);
                setExpandedSection(null);
                setSavedSelection(null);
                setOriginalHTML(null);
                setIsPreviewingFont(false);
            }
        };

        window.addEventListener('tiptap-editor-focus', handleOtherEditorFocus);
        return () => {
            window.removeEventListener('tiptap-editor-focus', handleOtherEditorFocus);
        };
    }, [isEditMode, editor]);
    
    // 🎯 Outside Click Handler - Deselect beim Klick außerhalb
    useEffect(() => {
        if (!isEditMode || !editor) return;

        const handleClickOutside = (event) => {
            // ✅ WICHTIG: Verwende wrapperRef für DIESEN spezifischen Editor (nicht querySelector!)
            const editorElement = wrapperRef.current;
            const toolbarElement = toolbarRef.current;
            
            // Klick war außerhalb von Editor UND Toolbar
            const clickedOutside = editorElement && !editorElement.contains(event.target) &&
                                  toolbarElement && !toolbarElement.contains(event.target);
            
            if (clickedOutside) {
                // Deselect: Blur Editor und verstecke Toolbar
                editor.commands.blur();
                setShowToolbar(false);
                setExpandedSection(null); // ✅ Schließe expanded sections
                setSavedSelection(null); // ✅ Reset saved selection
                
                // Optional: Callback für Parent Component
                if (onDeselect) {
                    onDeselect();
                }
            }
        };

        // ✅ FIX: 'click' statt 'mousedown' - schließt NUR bei echtem Click, nicht beim Scrollen!
        document.addEventListener('click', handleClickOutside);
        return () => {
            document.removeEventListener('click', handleClickOutside);
        };
    }, [isEditMode, editor, onDeselect]);
    
    // ✅ Reset expanded section when toolbar visibility changes
    useEffect(() => {
        if (!showToolbar) {
            setExpandedSection(null);
            setSavedSelection(null);
            setOriginalHTML(null);
        }
    }, [showToolbar]);
    
    // ✅ Restore original HTML wenn Font-Liste geschlossen wird OHNE Font-Auswahl
    useEffect(() => {
        // Wenn textFontFamily section geschlossen wird UND originalHTML existiert
        // → User hat Section geschlossen ohne Font zu wählen
        if (expandedSection !== 'textFontFamily' && originalHTML && editor && savedSelection) {
            console.log('🔄 Section closed without selection - restoring original HTML');
            
            // ✅ WICHTIG: Preview-Flag setzen während wir restoren
            setIsPreviewingFont(true);
            
            // ✅ WICHTIG: Selection SOFORT nach setContent wiederherstellen
            // damit die Toolbar nicht geschlossen wird
            editor.chain()
                .setContent(originalHTML)
                .setTextSelection({
                    from: savedSelection.from,
                    to: savedSelection.to
                })
                .run();
            
            setOriginalHTML(null);
            
            // Flag mit Delay zurücksetzen
            setTimeout(() => {
                setIsPreviewingFont(false);
            }, 100);
        }
    }, [expandedSection, originalHTML, editor, savedSelection]);

    // ✅ KRITISCH: Setze max-width mit !important für Live View (muss auf Top-Level sein!)
    useEffect(() => {
        // Nur im Live View Mode (nicht isEditMode)
        if (!isEditMode && liveViewRef.current && (currentMaxWidth || currentMaxWidthMobile)) {
            const activeMaxWidth = isMobileLayout ? (currentMaxWidthMobile || currentMaxWidth) : currentMaxWidth;
            liveViewRef.current.style.setProperty('max-width', `${activeMaxWidth}px`, 'important');
        }
    }, [isEditMode, currentMaxWidth, currentMaxWidthMobile, isMobileLayout]);

    // 🚫 LIVE VIEW: Normales HTML ohne TipTap
    if (!isEditMode) {
        const Element = element;
        
        // ✅ Live View Style - LEAN: Nutze style prop + nur notwendige Overrides
        const liveViewStyle = {
            ...style, // ✅ Enthält bereits fontSize, fontWeight, lineHeight, letterSpacing, textAlign, color
            // ✅ Width - ALWAYS apply if currentMaxWidth is set
            ...((currentMaxWidth || currentMaxWidthMobile) && element !== 'button' && {
                width: '100%'
                // maxWidth wird via setProperty mit !important gesetzt (siehe useEffect oben)
            }),
            // ✅ Margins hinzufügen (können nicht vom Parent kommen)
            marginTop: `${isMobileLayout ? (currentMarginsMobile.top ?? currentMargins.top ?? 0) : (currentMargins.top ?? 0)}px`,
            marginRight: `${isMobileLayout ? (currentMarginsMobile.right ?? currentMargins.right ?? 0) : (currentMargins.right ?? 0)}px`,
            marginBottom: `${isMobileLayout ? (currentMarginsMobile.bottom ?? currentMargins.bottom ?? 0) : (currentMargins.bottom ?? 0)}px`,
            marginLeft: `${isMobileLayout ? (currentMarginsMobile.left ?? currentMargins.left ?? 0) : (currentMargins.left ?? 0)}px`,
            // 🎯 Text Box Styling (Badge/Tag Style)
            ...(textBoxEnabled && {
                paddingTop: `${textBoxPaddingVertical || 6}px`,
                paddingBottom: `${textBoxPaddingVertical || 6}px`,
                paddingLeft: `${textBoxPaddingHorizontal || 12}px`,
                paddingRight: `${textBoxPaddingHorizontal || 12}px`,
                backgroundColor: textBoxBackgroundColor || 'transparent',
                borderRadius: `${textBoxBorderRadius || 8}px`,
                display: 'inline-block',
                border: textBoxBorderEnabled 
                    ? `${textBoxBorderWidth || 2}px solid ${textBoxBorderColor || '#ffffff'}`
                    : 'none',
                width: 'auto'
            })
        };
        
        return (
            <Element
                ref={liveViewRef}
                className={className}
                style={liveViewStyle}
                dangerouslySetInnerHTML={{ 
                    __html: value || placeholder 
                }}
            />
        );
    }

    // ✏️ EDIT MODE: TipTap mit Floating Toolbar
    if (!editor) {
        return null;
    }

    // 🎯 Kombiniere alle styles für den wrapper
    // ✅ KRITISCH: maxWidth muss NACH ...style kommen, damit es nicht überschrieben wird!
    const wrapperStyle = {
        position: 'relative',
        ...style, // ✅ Style Prop (Text-Styling)
        // ✅ Width & MaxWidth - ALWAYS apply if currentMaxWidth is set (not only when callback exists!)
        // ⚠️ CRITICAL FIX: In Preview Mode (isEditMode=false) sind keine Callbacks da, aber maxWidth muss trotzdem angewendet werden!
        ...((currentMaxWidth || currentMaxWidthMobile) && element !== 'button' && {
            width: '100%', // ✅ Volle Breite
            maxWidth: `${isMobileLayout ? (currentMaxWidthMobile || currentMaxWidth) : currentMaxWidth}px` // ✅ Aber limitiert
        }),
        // ✅ KRITISCH: Margins IMMER anwenden (auch im Live Mode!)
        marginTop: `${isMobileLayout ? (currentMarginsMobile.top ?? currentMargins.top ?? 0) : (currentMargins.top ?? 0)}px`,
        marginRight: `${isMobileLayout ? (currentMarginsMobile.right ?? currentMargins.right ?? 0) : (currentMargins.right ?? 0)}px`,
        marginBottom: `${isMobileLayout ? (currentMarginsMobile.bottom ?? currentMargins.bottom ?? 0) : (currentMargins.bottom ?? 0)}px`,
        marginLeft: `${isMobileLayout ? (currentMarginsMobile.left ?? currentMargins.left ?? 0) : (currentMargins.left ?? 0)}px`
    };

    // 🎯 CSS-Klassen kombinieren mit mobile-layout für responsive CSS + Animation
    // ✅ CRITICAL: Animation-Klassen NUR im Edit-Mode oder NACH Intersection Observer Trigger
    const wrapperClasses = [
        'tiptap-inline-wrapper',
        isMobileLayout && 'mobile-layout',
        // ✅ Animation-Klassen NUR wenn: (im Edit-Mode ODER Animation wurde getriggert)
        animationEnabled && (isEditMode || hasPlayedAnimation) && 'tiptap-text-animated',
        animationEnabled && (isEditMode || hasPlayedAnimation) && `tiptap-animation-${animationType}`,
        animationEnabled && (isEditMode || hasPlayedAnimation) && `tiptap-animation-speed-${animationSpeed}`
    ].filter(Boolean).join(' ');

    // 🎨 Toolbar Content (wird später über Portal gerendert)
    const renderToolbar = () => {
        if (!showToolbar || !showFloatingToolbar || !enableRichText) return null;

        return ReactDOM.createPortal(
            <div 
                ref={toolbarRef}
                className={`tiptap-bubble-menu-portal ${expandedSection ? 'expanded' : ''} ${isDragging ? 'dragging' : ''}`}
                style={{
                    position: 'fixed', // ✅ Fixed: Bleibt immer an der gleichen Stelle im Viewport
                    top: `${toolbarPosition.top}px`,
                    left: `${toolbarPosition.left}px`,
                    transform: 'translate(-50%, 0)',
                    zIndex: 999999
                }}
            >
                    <div className="tiptap-toolbar">
                        {/* 🎨 MAIN TOOLBAR BUTTONS - oben fixiert */}
                        <div className="tiptap-button-row">
                            {/* 🎯 Drag Handle Icon */}
                            <div 
                                className="tiptap-drag-handle"
                                style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
                                title="Drag to move toolbar"
                                onMouseDown={handleMouseDown}
                                onClick={(e) => {
                                    // ✅ FIX: Verhindere dass Click-Event die Toolbar schließt
                                    e.stopPropagation();
                                }}
                            >
                                <i className="fas fa-grip-vertical"></i>
                            </div>
                            {/* Bold */}
                            <button
                                onClick={() => {
                                    editor.chain().focus().toggleBold().run();
                                    setTimeout(() => onChange(editor.getHTML()), 50);
                                }}
                                className={`tiptap-btn ${editor.isActive('bold') ? 'is-active' : ''}`}
                                title="Bold (Ctrl+B)"
                            >
                                <i className="fas fa-bold"></i>
                            </button>

                            {/* Italic */}
                            <button
                                onClick={() => {
                                    editor.chain().focus().toggleItalic().run();
                                    setTimeout(() => onChange(editor.getHTML()), 50);
                                }}
                                className={`tiptap-btn ${editor.isActive('italic') ? 'is-active' : ''}`}
                                title="Italic (Ctrl+I)"
                            >
                                <i className="fas fa-italic"></i>
                            </button>

                            {/* Underline */}
                            <button
                                onClick={() => {
                                    editor.chain().focus().toggleUnderline().run();
                                    setTimeout(() => onChange(editor.getHTML()), 50);
                                }}
                                className={`tiptap-btn ${editor.isActive('underline') ? 'is-active' : ''}`}
                                title="Underline (Ctrl+U)"
                            >
                                <i className="fas fa-underline"></i>
                            </button>

                            {/* Strikethrough */}
                            <button
                                onClick={() => {
                                    editor.chain().focus().toggleStrike().run();
                                    setTimeout(() => onChange(editor.getHTML()), 50);
                                }}
                                className={`tiptap-btn ${editor.isActive('strike') ? 'is-active' : ''}`}
                                title="Strikethrough"
                            >
                                <i className="fas fa-strikethrough"></i>
                            </button>

                            {/* 🎯 QUICK ALIGNMENT TOGGLE - Ein Button der durch Left → Center → Right cycle */}
                            {(onTextAlignChange || onTextAlignMobileChange) && (
                                <button
                                    onClick={() => {
                                        const currentAlign = isMobileLayout ? currentTextAlignMobile : currentTextAlign;
                                        const callback = isMobileLayout ? onTextAlignMobileChange : onTextAlignChange;
                                        
                                        // Cycle: left → center → right → left
                                        const nextAlign = 
                                            currentAlign === 'left' ? 'center' :
                                            currentAlign === 'center' ? 'right' : 'left';
                                        
                                        if (callback) callback(nextAlign);
                                    }}
                                    className="tiptap-btn"
                                    title={`Text Alignment: ${isMobileLayout ? currentTextAlignMobile : currentTextAlign} (click to cycle)`}
                                >
                                    <i className={`fas fa-align-${isMobileLayout ? currentTextAlignMobile : currentTextAlign}`}></i>
                                </button>
                            )}

                            <div className="toolbar-separator" />

                            {/* Color Toggle */}
                            <button
                                onClick={() => {
                                    // Speichere aktuelle Selection bevor wir expandieren
                                    if (expandedSection !== 'colors') {
                                        const { from, to } = editor.state.selection;
                                        setSavedSelection({ from, to });
                                        console.log('💾 Selection saved:', { from, to });
                                    }
                                    setExpandedSection(expandedSection === 'colors' ? null : 'colors');
                                }}
                                className={`tiptap-btn ${expandedSection === 'colors' ? 'is-active' : ''}`}
                                title="Colors"
                            >
                                <i className="fas fa-palette"></i>
                            </button>

                            {/* Typography Toggle - Font Size, Weight, Line Height, Letter Spacing, Alignment & Width */}
                            {(onFontSizeChange || onLineHeightChange || onLetterSpacingChange || onTextAlignChange || onMaxWidthChange) && (
                                <button
                                    onClick={() => setExpandedSection(expandedSection === 'typography' ? null : 'typography')}
                                    className={`tiptap-btn ${expandedSection === 'typography' ? 'is-active' : ''}`}
                                    title="Typography"
                                >
                                    <span style={{ fontSize: '20px', fontWeight: '700' }}>Aa</span>
                                </button>
                            )}

                            {/* Margin Toggle */}
                            {onStyleChange && (
                                <button
                                    onClick={() => setExpandedSection(expandedSection === 'margins' ? null : 'margins')}
                                    className={`tiptap-btn ${expandedSection === 'margins' ? 'is-active' : ''}`}
                                    title="Spacing"
                                >
                                    <i className="fas fa-arrows-alt"></i>
                                </button>
                            )}

                            {/* Text Passage Font Family Toggle - Ändert nur selektierten Text */}
                            {availableFonts && availableFonts.length > 0 && (
                                <button
                                    onClick={() => {
                                        // Wenn Font-Liste ÖFFNET
                                        if (expandedSection !== 'textFontFamily') {
                                            const { from, to } = editor.state.selection;
                                            setSavedSelection({ from, to });
                                            
                                            // ✅ WICHTIG: Original HTML speichern für Preview-Reset
                                            const currentHTML = editor.getHTML();
                                            setOriginalHTML(currentHTML);
                                            console.log('💾 Selection & HTML saved for text font preview:', { from, to, html: currentHTML });
                                        } else {
                                            // Wenn Font-Liste SCHLIESST (ohne Click auf Font)
                                            // → Original HTML wird automatisch via useEffect restored
                                        }
                                        setExpandedSection(expandedSection === 'textFontFamily' ? null : 'textFontFamily');
                                    }}
                                    className={`tiptap-btn ${expandedSection === 'textFontFamily' ? 'is-active' : ''}`}
                                    title="Text Font (selected text only)"
                                >
                                    <i className="fas fa-font"></i>
                                </button>
                            )}

                            {/* Link Toggle */}
                            <button
                                onClick={() => {
                                    // Speichere Selection wenn Link-Editor öffnet
                                    if (expandedSection !== 'link') {
                                        const { from, to } = editor.state.selection;
                                        setSavedSelection({ from, to });
                                    }
                                    setExpandedSection(expandedSection === 'link' ? null : 'link');
                                }}
                                className={`tiptap-btn ${expandedSection === 'link' ? 'is-active' : ''} ${editor.isActive('link') ? 'has-link' : ''}`}
                                title="Link"
                            >
                                <i className="fas fa-link"></i>
                            </button>

                            {/* Effects Toggle - Combined Text Box + Animation */}
                            {(onTextBoxChange || onAnimationChange) && (
                                <button
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        setExpandedSection(expandedSection === 'effects' ? null : 'effects');
                                    }}
                                    className={`tiptap-btn ${expandedSection === 'effects' ? 'is-active' : ''} ${(textBoxEnabled || animationEnabled) ? 'has-feature' : ''}`}
                                    title="Effects (Text Box & Animation)"
                                >
                                    <i className="fas fa-magic"></i>
                                </button>
                            )}
                        </div>
                        
                        {/* 🎨 EXPANDABLE SECTIONS - klappen nach unten auf */}
                        
                        {/* 🎨 TYPOGRAPHY SECTION - Font Size, Weight, Line Height, Letter Spacing, Alignment, Max Width */}
                        {expandedSection === 'typography' && (onFontSizeChange || onLineHeightChange || onLetterSpacingChange || onTextAlignChange || onMaxWidthChange || 
                         onFontSizeMobileChange || onLineHeightMobileChange || onLetterSpacingMobileChange || onTextAlignMobileChange || onMaxWidthMobileChange) && (
                            <div className="tiptap-expanded-section tiptap-typography-section">
                                {/* Desktop/Mobile Toggle */}
                                <div className="floating-typo-device-row">
                                    <span className="floating-typo-device-label">Desktop</span>
                                    <div 
                                        className={`floating-typo-toggle ${typographyDevice === 'mobile' ? 'mobile' : ''}`}
                                        onClick={() => setTypographyDevice(typographyDevice === 'desktop' ? 'mobile' : 'desktop')}
                                    >
                                        <div className="floating-typo-toggle-slider"></div>
                                    </div>
                                    <span className="floating-typo-device-label">Mobile</span>
                                </div>

                                {/* Font Size Control */}
                                {((typographyDevice === 'desktop' && onFontSizeChange) || 
                                  (typographyDevice === 'mobile' && onFontSizeMobileChange)) && (
                                    <div className="typo-control">
                                        <label>Size</label>
                                        <input
                                            type="range"
                                            min="8"
                                            max="120"
                                            value={typographyDevice === 'desktop' ? currentFontSize : currentFontSizeMobile}
                                            onChange={(e) => {
                                                const newSize = parseInt(e.target.value);
                                                if (typographyDevice === 'desktop' && onFontSizeChange) {
                                                    onFontSizeChange(newSize);
                                                } else if (typographyDevice === 'mobile' && onFontSizeMobileChange) {
                                                    onFontSizeMobileChange(newSize);
                                                }
                                            }}
                                            className="typo-slider"
                                        />
                                        <span className="typo-val">
                                            {typographyDevice === 'desktop' ? currentFontSize : currentFontSizeMobile}px
                                        </span>
                                    </div>
                                )}

                                {/* Font Weight Control */}
                                {((typographyDevice === 'desktop' && onFontWeightChange) || 
                                  (typographyDevice === 'mobile' && onFontWeightMobileChange)) && (
                                    <div className="typo-control">
                                        <label>Weight</label>
                                        <input
                                            type="range"
                                            min="300"
                                            max="900"
                                            step="100"
                                            value={typographyDevice === 'desktop' ? (currentFontWeight || 400) : (currentFontWeightMobile || 400)}
                                            onChange={(e) => {
                                                const newWeight = e.target.value;
                                                if (typographyDevice === 'desktop' && onFontWeightChange) {
                                                    onFontWeightChange(newWeight);
                                                } else if (typographyDevice === 'mobile' && onFontWeightMobileChange) {
                                                    onFontWeightMobileChange(newWeight);
                                                }
                                            }}
                                            className="typo-slider"
                                        />
                                        <span className="typo-val">
                                            {typographyDevice === 'desktop' ? (currentFontWeight || 400) : (currentFontWeightMobile || 400)}
                                        </span>
                                    </div>
                                )}

                                {/* Line Height */}
                                {((typographyDevice === 'desktop' && onLineHeightChange) || 
                                  (typographyDevice === 'mobile' && onLineHeightMobileChange)) && (
                                    <div className="typo-control">
                                        <label>Line</label>
                                        <input
                                            type="range"
                                            min="0.8"
                                            max="3"
                                            step="0.1"
                                            value={typographyDevice === 'desktop' ? currentLineHeight : currentLineHeightMobile}
                                            onChange={(e) => {
                                                const newValue = parseFloat(e.target.value);
                                                if (typographyDevice === 'desktop' && onLineHeightChange) {
                                                    onLineHeightChange(newValue);
                                                } else if (typographyDevice === 'mobile' && onLineHeightMobileChange) {
                                                    onLineHeightMobileChange(newValue);
                                                }
                                            }}
                                            className="typo-slider"
                                        />
                                        <span className="typo-val">
                                            {typographyDevice === 'desktop' ? currentLineHeight : currentLineHeightMobile}
                                        </span>
                                    </div>
                                )}

                                {/* Letter Spacing */}
                                {((typographyDevice === 'desktop' && onLetterSpacingChange) || 
                                  (typographyDevice === 'mobile' && onLetterSpacingMobileChange)) && (
                                    <div className="typo-control">
                                        <label>Space</label>
                                        <input
                                            type="range"
                                            min="-5"
                                            max="20"
                                            step="0.5"
                                            value={typographyDevice === 'desktop' ? currentLetterSpacing : currentLetterSpacingMobile}
                                            onChange={(e) => {
                                                const newValue = parseFloat(e.target.value);
                                                if (typographyDevice === 'desktop' && onLetterSpacingChange) {
                                                    onLetterSpacingChange(newValue);
                                                } else if (typographyDevice === 'mobile' && onLetterSpacingMobileChange) {
                                                    onLetterSpacingMobileChange(newValue);
                                                }
                                            }}
                                            className="typo-slider"
                                        />
                                        <span className="typo-val">
                                            {typographyDevice === 'desktop' ? currentLetterSpacing : currentLetterSpacingMobile}
                                        </span>
                                    </div>
                                )}

                                {/* Text Alignment */}
                                {((typographyDevice === 'desktop' && onTextAlignChange) || 
                                  (typographyDevice === 'mobile' && onTextAlignMobileChange)) && (
                                    <div className="typo-control">
                                        <label>Align</label>
                                        <div className="typo-align-buttons">
                                            <button
                                                className={`typo-align-btn ${(typographyDevice === 'desktop' ? currentTextAlign : currentTextAlignMobile) === 'left' ? 'active' : ''}`}
                                                onClick={() => {
                                                    if (typographyDevice === 'desktop' && onTextAlignChange) {
                                                        onTextAlignChange('left');
                                                    } else if (typographyDevice === 'mobile' && onTextAlignMobileChange) {
                                                        onTextAlignMobileChange('left');
                                                    }
                                                }}
                                                title="Align Left"
                                            >
                                                <i className="fas fa-align-left"></i>
                                            </button>
                                            <button
                                                className={`typo-align-btn ${(typographyDevice === 'desktop' ? currentTextAlign : currentTextAlignMobile) === 'center' ? 'active' : ''}`}
                                                onClick={() => {
                                                    if (typographyDevice === 'desktop' && onTextAlignChange) {
                                                        onTextAlignChange('center');
                                                    } else if (typographyDevice === 'mobile' && onTextAlignMobileChange) {
                                                        onTextAlignMobileChange('center');
                                                    }
                                                }}
                                                title="Align Center"
                                            >
                                                <i className="fas fa-align-center"></i>
                                            </button>
                                            <button
                                                className={`typo-align-btn ${(typographyDevice === 'desktop' ? currentTextAlign : currentTextAlignMobile) === 'right' ? 'active' : ''}`}
                                                onClick={() => {
                                                    if (typographyDevice === 'desktop' && onTextAlignChange) {
                                                        onTextAlignChange('right');
                                                    } else if (typographyDevice === 'mobile' && onTextAlignMobileChange) {
                                                        onTextAlignMobileChange('right');
                                                    }
                                                }}
                                                title="Align Right"
                                            >
                                                <i className="fas fa-align-right"></i>
                                            </button>
                                        </div>
                                    </div>
                                )}

                                {/* Max Width Control */}
                                {((typographyDevice === 'desktop' && onMaxWidthChange) || 
                                  (typographyDevice === 'mobile' && onMaxWidthMobileChange)) && (
                                    <div className="typo-control">
                                        <label>Width</label>
                                        <input
                                            type="range"
                                            min="100"
                                            max="1400"
                                            step="10"
                                            value={typographyDevice === 'desktop' ? currentMaxWidth : currentMaxWidthMobile}
                                            onChange={(e) => {
                                                const newWidth = parseInt(e.target.value);
                                                if (typographyDevice === 'desktop' && onMaxWidthChange) {
                                                    onMaxWidthChange(newWidth);
                                                } else if (typographyDevice === 'mobile' && onMaxWidthMobileChange) {
                                                    onMaxWidthMobileChange(newWidth);
                                                }
                                            }}
                                            className="typo-slider"
                                        />
                                        <span className="typo-val">
                                            {typographyDevice === 'desktop' ? currentMaxWidth : currentMaxWidthMobile}px
                                        </span>
                                    </div>
                                )}
                                
                                <span className="section-label">Typography</span>
                            </div>
                        )}
                        
                        {expandedSection === 'colors' && colors && Object.keys(colors).length > 0 && (
                            <div className="tiptap-expanded-section">
                                {/* 🎨 BRAND COLORS ROW */}
                                <div className="tiptap-brand-colors">
                                    {Object.values(colors).map((brandColor, index) => (
                                        <button
                                            key={index}
                                            type="button"
                                            className="tiptap-brand-color"
                                            style={{ backgroundColor: brandColor }}
                                            onClick={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                
                                                // Restore selection
                                                if (savedSelection && savedSelection.from !== savedSelection.to) {
                                                    editor.commands.setTextSelection({
                                                        from: savedSelection.from,
                                                        to: savedSelection.to
                                                    });
                                                }
                                                
                                                // Check selection
                                                const { from, to } = editor.state.selection;
                                                const hasSelection = from !== to;
                                                
                                                if (!hasSelection) {
                                                    console.warn('⚠️ No text selected for color change!');
                                                    alert('Please select text first to apply color');
                                                    return;
                                                }
                                                
                                                // Apply color
                                                editor.chain().focus().setColor(brandColor).run();
                                                
                                                // Save immediately
                                                setTimeout(() => {
                                                    const html = editor.getHTML();
                                                    console.log('✅ Color applied, HTML:', html);
                                                    onChange(html);
                                                }, 50);
                                            }}
                                            title={`Brand Color ${index + 1}: ${brandColor}`}
                                        />
                                    ))}
                                </div>
                                
                                {/* 🎨 CUSTOM COLOR SLOTS - 5 fixe Slots mit Double-Click zum Editieren */}
                                <div className="tiptap-custom-color-slots">
                                    {customColorSlots.map((slotColor, slotIndex) => (
                                        <button
                                            key={slotIndex}
                                            type="button"
                                            className="tiptap-brand-color tiptap-color-slot"
                                            style={{ 
                                                backgroundColor: slotColor,
                                                border: slotColor === '#ffffff' ? '2px dashed rgba(255, 255, 255, 0.3)' : '2px solid rgba(255, 255, 255, 0.3)'
                                            }}
                                            onClick={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                
                                                // Single-Click: Wenn Slot gefüllt → Apply Color to Text
                                                if (slotColor !== '#ffffff') {
                                                    // Restore selection
                                                    if (savedSelection && savedSelection.from !== savedSelection.to) {
                                                        editor.commands.setTextSelection({
                                                            from: savedSelection.from,
                                                            to: savedSelection.to
                                                        });
                                                    }
                                                    
                                                    // Check selection
                                                    const { from, to } = editor.state.selection;
                                                    const hasSelection = from !== to;
                                                    
                                                    if (!hasSelection) {
                                                        console.warn('⚠️ No text selected for custom color!');
                                                        alert('Please select text first to apply custom color');
                                                        return;
                                                    }
                                                    
                                                    // Apply custom color
                                                    editor.chain().focus().setColor(slotColor).run();
                                                    
                                                    // Save immediately
                                                    setTimeout(() => {
                                                        const html = editor.getHTML();
                                                        console.log('✅ Custom color applied, HTML:', html);
                                                        onChange(html);
                                                    }, 50);
                                                } else {
                                                    // Slot ist leer → Open Color Picker
                                                    setEditingSlotIndex(slotIndex);
                                                    const colorInput = document.getElementById(`tiptap-color-slot-${slotIndex}`);
                                                    if (colorInput) {
                                                        colorInput.click();
                                                    }
                                                }
                                            }}
                                            onDoubleClick={(e) => {
                                                // Double-Click: IMMER Color Picker öffnen (zum Editieren)
                                                e.preventDefault();
                                                e.stopPropagation();
                                                setEditingSlotIndex(slotIndex);
                                                const colorInput = document.getElementById(`tiptap-color-slot-${slotIndex}`);
                                                if (colorInput) {
                                                    colorInput.click();
                                                }
                                            }}
                                            title={slotColor === '#ffffff' 
                                                ? 'Click to set custom color' 
                                                : `Custom Color: ${slotColor}\nClick: Apply to text\nDouble-Click: Edit color`
                                            }
                                        >
                                            {slotColor === '#ffffff' && (
                                                <i className="fas fa-plus" style={{ fontSize: '10px', color: 'rgba(255, 255, 255, 0.5)' }}></i>
                                            )}
                                        </button>
                                    ))}
                                    
                                    {/* Versteckte Color Picker Inputs - Einer pro Slot */}
                                    {customColorSlots.map((slotColor, slotIndex) => (
                                        <input
                                            key={slotIndex}
                                            id={`tiptap-color-slot-${slotIndex}`}
                                            type="color"
                                            value={slotColor}
                                            style={{ display: 'none' }}
                                            onChange={(e) => {
                                                const newColor = e.target.value;
                                                updateColorSlot(slotIndex, newColor);
                                            }}
                                        />
                                    ))}
                                </div>
                                
                                <span className="section-label">Brand & Custom Colors</span>
                            </div>
                        )}

                        {expandedSection === 'margins' && (onStyleChange || onStyleChangeMobile) && (
                            <div className="tiptap-expanded-section tiptap-typography-section">
                                {/* Desktop/Mobile Toggle */}
                                <div className="floating-typo-device-row">
                                    <span className="floating-typo-device-label">Desktop</span>
                                    <div 
                                        className={`floating-typo-toggle ${typographyDevice === 'mobile' ? 'mobile' : ''}`}
                                        onClick={() => setTypographyDevice(typographyDevice === 'desktop' ? 'mobile' : 'desktop')}
                                    >
                                        <div className="floating-typo-toggle-slider"></div>
                                    </div>
                                    <span className="floating-typo-device-label">Mobile</span>
                                </div>
                                
                                {/* Margin Top */}
                                {((typographyDevice === 'desktop' && onStyleChange) || 
                                  (typographyDevice === 'mobile' && onStyleChangeMobile)) && (
                                    <div className="typo-control">
                                        <label>Top</label>
                                        <input
                                            type="range"
                                            min="0"
                                            max="200"
                                            value={typographyDevice === 'desktop' ? (currentMargins.top || 0) : (currentMarginsMobile.top || 0)}
                                            onChange={(e) => {
                                                const newValue = parseInt(e.target.value) || 0;
                                                if (typographyDevice === 'desktop' && onStyleChange) {
                                                    onStyleChange({ ...currentMargins, top: newValue });
                                                } else if (typographyDevice === 'mobile' && onStyleChangeMobile) {
                                                    onStyleChangeMobile({ ...currentMarginsMobile, top: newValue });
                                                }
                                            }}
                                            className="typo-slider"
                                        />
                                        <span className="typo-val">
                                            {typographyDevice === 'desktop' ? (currentMargins.top || 0) : (currentMarginsMobile.top || 0)}px
                                        </span>
                                    </div>
                                )}
                                
                                {/* Margin Bottom */}
                                {((typographyDevice === 'desktop' && onStyleChange) || 
                                  (typographyDevice === 'mobile' && onStyleChangeMobile)) && (
                                    <div className="typo-control">
                                        <label>Bottom</label>
                                        <input
                                            type="range"
                                            min="0"
                                            max="200"
                                            value={typographyDevice === 'desktop' ? (currentMargins.bottom || 0) : (currentMarginsMobile.bottom || 0)}
                                            onChange={(e) => {
                                                const newValue = parseInt(e.target.value) || 0;
                                                if (typographyDevice === 'desktop' && onStyleChange) {
                                                    onStyleChange({ ...currentMargins, bottom: newValue });
                                                } else if (typographyDevice === 'mobile' && onStyleChangeMobile) {
                                                    onStyleChangeMobile({ ...currentMarginsMobile, bottom: newValue });
                                                }
                                            }}
                                            className="typo-slider"
                                        />
                                        <span className="typo-val">
                                            {typographyDevice === 'desktop' ? (currentMargins.bottom || 0) : (currentMarginsMobile.bottom || 0)}px
                                        </span>
                                    </div>
                                )}
                                
                                {/* Margin Left */}
                                {((typographyDevice === 'desktop' && onStyleChange) || 
                                  (typographyDevice === 'mobile' && onStyleChangeMobile)) && (
                                    <div className="typo-control">
                                        <label>Left</label>
                                        <input
                                            type="range"
                                            min="0"
                                            max="200"
                                            value={typographyDevice === 'desktop' ? (currentMargins.left || 0) : (currentMarginsMobile.left || 0)}
                                            onChange={(e) => {
                                                const newValue = parseInt(e.target.value) || 0;
                                                if (typographyDevice === 'desktop' && onStyleChange) {
                                                    onStyleChange({ ...currentMargins, left: newValue });
                                                } else if (typographyDevice === 'mobile' && onStyleChangeMobile) {
                                                    onStyleChangeMobile({ ...currentMarginsMobile, left: newValue });
                                                }
                                            }}
                                            className="typo-slider"
                                        />
                                        <span className="typo-val">
                                            {typographyDevice === 'desktop' ? (currentMargins.left || 0) : (currentMarginsMobile.left || 0)}px
                                        </span>
                                    </div>
                                )}
                                
                                {/* Margin Right */}
                                {((typographyDevice === 'desktop' && onStyleChange) || 
                                  (typographyDevice === 'mobile' && onStyleChangeMobile)) && (
                                    <div className="typo-control">
                                        <label>Right</label>
                                        <input
                                            type="range"
                                            min="0"
                                            max="200"
                                            value={typographyDevice === 'desktop' ? (currentMargins.right || 0) : (currentMarginsMobile.right || 0)}
                                            onChange={(e) => {
                                                const newValue = parseInt(e.target.value) || 0;
                                                if (typographyDevice === 'desktop' && onStyleChange) {
                                                    onStyleChange({ ...currentMargins, right: newValue });
                                                } else if (typographyDevice === 'mobile' && onStyleChangeMobile) {
                                                    onStyleChangeMobile({ ...currentMarginsMobile, right: newValue });
                                                }
                                            }}
                                            className="typo-slider"
                                        />
                                        <span className="typo-val">
                                            {typographyDevice === 'desktop' ? (currentMargins.right || 0) : (currentMarginsMobile.right || 0)}px
                                        </span>
                                    </div>
                                )}
                                
                                <span className="section-label">Spacing</span>
                            </div>
                        )}



                        {/* 🎨 OLD TEXT BOX SECTION - REMOVED (now in Effects section) */}
                        {false && expandedSection === 'textBox' && onTextBoxChange && (
                            <div className="tiptap-expanded-section">
                                {/* Toggle Enable/Disable */}
                                <div className="button-toggle-row">
                                    <span className="button-toggle-label">Enable Text Box</span>
                                    <label className="button-toggle-switch">
                                        <input
                                            type="checkbox"
                                            checked={textBoxEnabled}
                                            onChange={(e) => {
                                                onTextBoxChange({ textBoxEnabled: e.target.checked });
                                            }}
                                        />
                                        <span className="button-toggle-slider"></span>
                                    </label>
                                </div>
                                
                                {textBoxEnabled && (
                                    <>
                                        {/* Padding Horizontal */}
                                        <div className="button-slider-control">
                                            <label className="button-slider-label">Pad ↔</label>
                                            <input
                                                type="range"
                                                min="0"
                                                max="50"
                                                step="1"
                                                value={textBoxPaddingHorizontal || 12}
                                                onChange={(e) => {
                                                    const val = parseInt(e.target.value);
                                                    onTextBoxChange({ textBoxPaddingHorizontal: val });
                                                }}
                                                className="button-slider"
                                            />
                                            <span className="button-slider-value">{textBoxPaddingHorizontal || 12}px</span>
                                        </div>
                                        
                                        {/* Padding Vertical */}
                                        <div className="button-slider-control">
                                            <label className="button-slider-label">Pad ↕</label>
                                            <input
                                                type="range"
                                                min="0"
                                                max="40"
                                                step="1"
                                                value={textBoxPaddingVertical || 6}
                                                onChange={(e) => {
                                                    const val = parseInt(e.target.value);
                                                    onTextBoxChange({ textBoxPaddingVertical: val });
                                                }}
                                                className="button-slider"
                                            />
                                            <span className="button-slider-value">{textBoxPaddingVertical || 6}px</span>
                                        </div>
                                        
                                        {/* Background Color - Mit Brand Colors & Custom Slots */}
                                        <div style={{ marginBottom: '12px' }}>
                                            <div className="button-slider-label" style={{ marginBottom: '8px' }}>Background</div>
                                            
                                            {/* Brand Colors */}
                                            {colors && Object.keys(colors).length > 0 && (
                                                <div style={{ marginBottom: '8px' }}>
                                                    <div className="tiptap-brand-colors" style={{ justifyContent: 'flex-start', gap: '6px' }}>
                                                        {Object.values(colors).map((brandColor, index) => (
                                                            <button
                                                                key={index}
                                                                type="button"
                                                                className="tiptap-brand-color"
                                                                style={{ 
                                                                    backgroundColor: brandColor,
                                                                    width: '24px',
                                                                    height: '24px',
                                                                    border: textBoxBackgroundColor === brandColor ? '2px solid white' : '2px solid rgba(255, 255, 255, 0.3)'
                                                                }}
                                                                onClick={(e) => {
                                                                    e.preventDefault();
                                                                    e.stopPropagation();
                                                                    onTextBoxChange({ textBoxBackgroundColor: brandColor });
                                                                }}
                                                                title={`Brand Color ${index + 1}`}
                                                            />
                                                        ))}
                                                    </div>
                                                </div>
                                            )}
                                            
                                            {/* Custom Color Slots */}
                                            <div style={{ marginBottom: '8px' }}>
                                                <div className="tiptap-brand-colors" style={{ justifyContent: 'flex-start', gap: '6px' }}>
                                                    {customColorSlots.map((slotColor, index) => (
                                                        <button
                                                            key={index}
                                                            type="button"
                                                            className="tiptap-brand-color"
                                                            style={{ 
                                                                backgroundColor: slotColor,
                                                                width: '24px',
                                                                height: '24px',
                                                                border: textBoxBackgroundColor === slotColor ? '2px solid white' : '2px solid rgba(255, 255, 255, 0.3)',
                                                                position: 'relative'
                                                            }}
                                                            onClick={(e) => {
                                                                e.preventDefault();
                                                                e.stopPropagation();
                                                                onTextBoxChange({ textBoxBackgroundColor: slotColor });
                                                            }}
                                                            title={`Custom Color ${index + 1}`}
                                                        />
                                                    ))}
                                                </div>
                                            </div>
                                            
                                            {/* Color Picker */}
                                            <div className="button-color-picker-row">
                                                <label className="button-slider-label">Custom</label>
                                                <input
                                                    type="color"
                                                    value={textBoxBackgroundColor || '#000000'}
                                                    onChange={(e) => {
                                                        const newColor = e.target.value;
                                                        onTextBoxChange({ textBoxBackgroundColor: newColor });
                                                        
                                                        // Füge zu Custom Slots hinzu wenn neu
                                                        if (!customColorSlots.includes(newColor) && !Object.values(colors).includes(newColor)) {
                                                            const newSlots = [...customColorSlots];
                                                            newSlots.pop(); // Letzten Slot entfernen
                                                            newSlots.unshift(newColor); // Neue Farbe am Anfang
                                                            setCustomColorSlots(newSlots);
                                                        }
                                                    }}
                                                    className="button-color-picker"
                                                />
                                                <span className="button-slider-value" style={{ fontSize: '9px', textTransform: 'uppercase' }}>
                                                    {textBoxBackgroundColor || '#000'}
                                                </span>
                                            </div>
                                        </div>
                                        
                                        {/* Border Toggle */}
                                        <div className="button-toggle-row">
                                            <span className="button-toggle-label">Border</span>
                                            <label className="button-toggle-switch">
                                                <input
                                                    type="checkbox"
                                                    checked={textBoxBorderEnabled}
                                                    onChange={(e) => {
                                                        onTextBoxChange({ textBoxBorderEnabled: e.target.checked });
                                                    }}
                                                />
                                                <span className="button-toggle-slider"></span>
                                            </label>
                                        </div>
                                        
                                        {textBoxBorderEnabled && (
                                            <>
                                                {/* Border Width */}
                                                <div className="button-slider-control">
                                                    <label className="button-slider-label">Width</label>
                                                    <input
                                                        type="range"
                                                        min="1"
                                                        max="10"
                                                        step="1"
                                                        value={textBoxBorderWidth || 2}
                                                        onChange={(e) => {
                                                            const val = parseInt(e.target.value);
                                                            onTextBoxChange({ textBoxBorderWidth: val });
                                                        }}
                                                        className="button-slider"
                                                    />
                                                    <span className="button-slider-value">{textBoxBorderWidth || 2}px</span>
                                                </div>
                                                
                                                {/* Border Color - Mit Brand Colors & Custom Slots */}
                                                <div style={{ marginBottom: '12px' }}>
                                                    <div className="button-slider-label" style={{ marginBottom: '8px' }}>Border Color</div>
                                                    
                                                    {/* Brand Colors */}
                                                    {colors && Object.keys(colors).length > 0 && (
                                                        <div style={{ marginBottom: '8px' }}>
                                                            <div className="tiptap-brand-colors" style={{ justifyContent: 'flex-start', gap: '6px' }}>
                                                                {Object.values(colors).map((brandColor, index) => (
                                                                    <button
                                                                        key={index}
                                                                        type="button"
                                                                        className="tiptap-brand-color"
                                                                        style={{ 
                                                                            backgroundColor: brandColor,
                                                                            width: '24px',
                                                                            height: '24px',
                                                                            border: textBoxBorderColor === brandColor ? '2px solid white' : '2px solid rgba(255, 255, 255, 0.3)'
                                                                        }}
                                                                        onClick={(e) => {
                                                                            e.preventDefault();
                                                                            e.stopPropagation();
                                                                            onTextBoxChange({ textBoxBorderColor: brandColor });
                                                                        }}
                                                                        title={`Brand Color ${index + 1}`}
                                                                    />
                                                                ))}
                                                            </div>
                                                        </div>
                                                    )}
                                                    
                                                    {/* Custom Color Slots */}
                                                    <div style={{ marginBottom: '8px' }}>
                                                        <div className="tiptap-brand-colors" style={{ justifyContent: 'flex-start', gap: '6px' }}>
                                                            {customColorSlots.map((slotColor, index) => (
                                                                <button
                                                                    key={index}
                                                                    type="button"
                                                                    className="tiptap-brand-color"
                                                                    style={{ 
                                                                        backgroundColor: slotColor,
                                                                        width: '24px',
                                                                        height: '24px',
                                                                        border: textBoxBorderColor === slotColor ? '2px solid white' : '2px solid rgba(255, 255, 255, 0.3)',
                                                                        position: 'relative'
                                                                    }}
                                                                    onClick={(e) => {
                                                                        e.preventDefault();
                                                                        e.stopPropagation();
                                                                        onTextBoxChange({ textBoxBorderColor: slotColor });
                                                                    }}
                                                                    title={`Custom Color ${index + 1}`}
                                                                />
                                                            ))}
                                                        </div>
                                                    </div>
                                                    
                                                    {/* Color Picker */}
                                                    <div className="button-color-picker-row">
                                                        <label className="button-slider-label">Custom</label>
                                                        <input
                                                            type="color"
                                                            value={textBoxBorderColor || '#ffffff'}
                                                            onChange={(e) => {
                                                                const newColor = e.target.value;
                                                                onTextBoxChange({ textBoxBorderColor: newColor });
                                                                
                                                                // Füge zu Custom Slots hinzu wenn neu
                                                                if (!customColorSlots.includes(newColor) && !Object.values(colors).includes(newColor)) {
                                                                    const newSlots = [...customColorSlots];
                                                                    newSlots.pop(); // Letzten Slot entfernen
                                                                    newSlots.unshift(newColor); // Neue Farbe am Anfang
                                                                    setCustomColorSlots(newSlots);
                                                                }
                                                            }}
                                                            className="button-color-picker"
                                                        />
                                                        <span className="button-slider-value" style={{ fontSize: '9px', textTransform: 'uppercase' }}>
                                                            {textBoxBorderColor || '#fff'}
                                                        </span>
                                                    </div>
                                                </div>
                                            </>
                                        )}
                                        
                                        {/* Border Radius */}
                                        <div className="button-slider-control">
                                            <label className="button-slider-label">Radius</label>
                                            <input
                                                type="range"
                                                min="0"
                                                max="50"
                                                step="1"
                                                value={textBoxBorderRadius || 8}
                                                onChange={(e) => {
                                                    const val = parseInt(e.target.value);
                                                    onTextBoxChange({ textBoxBorderRadius: val });
                                                }}
                                                className="button-slider"
                                            />
                                            <span className="button-slider-value">{textBoxBorderRadius || 8}px</span>
                                        </div>
                                    </>
                                )}
                                
                                <span className="section-label">TEXT BOX</span>
                            </div>
                        )}

                        {/* 🎨 TEXT PASSAGE FONT FAMILY - Ändert nur selektierten Text MIT LIVE PREVIEW */}
                        {expandedSection === 'textFontFamily' && availableFonts && availableFonts.length > 0 && (() => {
                            return (
                            <div className="tiptap-expanded-section">
                                <div className="tiptap-font-family-list">
                                    {/* ⭐ BRAND FONTS ZUERST - Mit Badge */}
                                    {brandFonts?.globalBigFont && (
                                        <button
                                            key="brand-big-font"
                                            type="button"
                                            className={`tiptap-font-family-option brand-font ${editor.isActive('textStyle', { fontFamily: brandFonts.globalBigFont }) ? 'is-active' : ''}`}
                                            style={{ fontFamily: brandFonts.globalBigFont }}
                                            onMouseEnter={() => {
                                                // ❌ HOVER PREVIEW DEAKTIVIERT - zu viele Re-Renders!
                                            }}
                                            onMouseLeave={() => {
                                                // ❌ HOVER PREVIEW DEAKTIVIERT
                                            }}
                                            onClick={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                
                                                if (savedSelection && savedSelection.from !== savedSelection.to) {
                                                    editor.commands.setTextSelection({
                                                        from: savedSelection.from,
                                                        to: savedSelection.to
                                                    });
                                                }
                                                
                                                const { from, to } = editor.state.selection;
                                                const hasSelection = from !== to;
                                                
                                                if (!hasSelection) {
                                                    alert('Please select text first to apply font');
                                                    return;
                                                }
                                                
                                                editor.chain().focus().setFontFamily(brandFonts.globalBigFont).run();
                                                setOriginalHTML(null);
                                                setIsPreviewingFont(false);
                                                
                                                setTimeout(() => {
                                                    const html = editor.getHTML();
                                                    onChange(html);
                                                }, 50);
                                                
                                                // ✅ Font Selector bleibt offen für weitere Font-Änderungen
                                                // setExpandedSection(null); // ❌ ENTFERNT
                                                setSavedSelection(null);
                                            }}
                                            title={`${brandFonts.globalBigFont} (Brand Headline Font)`}
                                        >
                                            ⭐ {brandFonts.globalBigFont}
                                        </button>
                                    )}
                                    
                                    {brandFonts?.globalSmallFont && brandFonts.globalSmallFont !== brandFonts.globalBigFont && (
                                        <button
                                            key="brand-small-font"
                                            type="button"
                                            className={`tiptap-font-family-option brand-font ${editor.isActive('textStyle', { fontFamily: brandFonts.globalSmallFont }) ? 'is-active' : ''}`}
                                            style={{ fontFamily: brandFonts.globalSmallFont }}
                                            onMouseEnter={() => {
                                                // ❌ HOVER PREVIEW DEAKTIVIERT - zu viele Re-Renders!
                                            }}
                                            onMouseLeave={() => {
                                                // ❌ HOVER PREVIEW DEAKTIVIERT
                                            }}
                                            onClick={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                
                                                if (savedSelection && savedSelection.from !== savedSelection.to) {
                                                    editor.commands.setTextSelection({
                                                        from: savedSelection.from,
                                                        to: savedSelection.to
                                                    });
                                                }
                                                
                                                const { from, to } = editor.state.selection;
                                                const hasSelection = from !== to;
                                                
                                                if (!hasSelection) {
                                                    alert('Please select text first to apply font');
                                                    return;
                                                }
                                                
                                                editor.chain().focus().setFontFamily(brandFonts.globalSmallFont).run();
                                                setOriginalHTML(null);
                                                setIsPreviewingFont(false);
                                                
                                                setTimeout(() => {
                                                    const html = editor.getHTML();
                                                    onChange(html);
                                                }, 50);
                                                
                                                // ✅ Font Selector bleibt offen
                                                // setExpandedSection(null); // ❌ ENTFERNT
                                                setSavedSelection(null);
                                            }}
                                            title={`${brandFonts.globalSmallFont} (Brand Body Font)`}
                                        >
                                            ⭐ {brandFonts.globalSmallFont}
                                        </button>
                                    )}
                                    
                                    {/* Divider nach Brand Fonts */}
                                    {(brandFonts?.globalBigFont || brandFonts?.globalSmallFont) && (
                                        <div style={{
                                            height: '1px',
                                            background: 'rgba(255, 255, 255, 0.2)',
                                            margin: '8px 0'
                                        }}></div>
                                    )}
                                    
                                    {/* Alle anderen Fonts */}
                                    {availableFonts.filter(font => 
                                        font !== brandFonts?.globalBigFont && 
                                        font !== brandFonts?.globalSmallFont
                                    ).map((font, index) => (
                                        <button
                                            key={index}
                                            type="button"
                                            className={`tiptap-font-family-option ${editor.isActive('textStyle', { fontFamily: font }) ? 'is-active' : ''}`}
                                            style={{ fontFamily: font }}
                                            onMouseEnter={() => {
                                                // ❌ HOVER PREVIEW DEAKTIVIERT - zu viele Re-Renders!
                                            }}
                                            onMouseLeave={() => {
                                                // ❌ HOVER PREVIEW DEAKTIVIERT
                                            }}
                                            onClick={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                
                                                // Nutze gespeicherte Selection falls vorhanden
                                                if (savedSelection && savedSelection.from !== savedSelection.to) {
                                                    // Restore selection
                                                    editor.commands.setTextSelection({
                                                        from: savedSelection.from,
                                                        to: savedSelection.to
                                                    });
                                                }
                                                
                                                // Check if we now have a selection
                                                const { from, to } = editor.state.selection;
                                                const hasSelection = from !== to;
                                                
                                                if (!hasSelection) {
                                                    alert('Please select text first to apply font');
                                                    return;
                                                }
                                                
                                                // Apply font family to selected text
                                                editor.chain().focus().setFontFamily(font).run();
                                                
                                                // ✅ WICHTIG: Original HTML löschen (Font wurde permanent gewählt)
                                                setOriginalHTML(null);
                                                setIsPreviewingFont(false); // ✅ Preview beendet
                                                
                                                // Save immediately
                                                setTimeout(() => {
                                                    const html = editor.getHTML();
                                                    onChange(html);
                                                }, 50);
                                                
                                                // ✅ Font Selector bleibt offen
                                                // setExpandedSection(null); // ❌ ENTFERNT
                                                setSavedSelection(null);
                                            }}
                                            title={font}
                                        >
                                            {font}
                                        </button>
                                    ))}
                                </div>
                                <span className="section-label">Text Font</span>
                            </div>
                            );
                        })()}

                        {/* 🔗 LINK SECTION */}
                        {expandedSection === 'link' && (() => {
                            return (
                                <div className="tiptap-expanded-section tiptap-link-section">
                                    <div className="tiptap-link-input-wrapper">
                                        <label className="tiptap-link-label">
                                            <i className="fas fa-link"></i> Link URL
                                        </label>
                                        <input
                                            type="text"
                                            className="tiptap-link-input"
                                            value={linkUrl}
                                            onChange={(e) => setLinkUrl(e.target.value)}
                                            placeholder="https://example.com"
                                            autoFocus
                                        />
                                        
                                        {/* Link Actions */}
                                        <div className="tiptap-link-actions">
                                            {/* Set Link Button */}
                                            <button
                                                type="button"
                                                className="tiptap-link-set-btn"
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    
                                                    if (!linkUrl || linkUrl.trim() === '') {
                                                        alert('Please enter a URL');
                                                        return;
                                                    }
                                                    
                                                    // Restore selection
                                                    if (savedSelection) {
                                                        editor.commands.setTextSelection(savedSelection);
                                                    }
                                                    
                                                    // Set link
                                                    editor.chain().focus().setLink({ href: linkUrl }).run();
                                                    
                                                    // Save HTML
                                                    setTimeout(() => onChange(editor.getHTML()), 50);
                                                    
                                                    // Close section
                                                    setExpandedSection(null);
                                                    setSavedSelection(null);
                                                }}
                                            >
                                                <i className="fas fa-check"></i> Set Link
                                            </button>
                                            
                                            {/* Remove Link Button */}
                                            {editor.isActive('link') && (
                                                <button
                                                    type="button"
                                                    className="tiptap-link-remove-btn"
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                        
                                                        // Restore selection
                                                        if (savedSelection) {
                                                            editor.commands.setTextSelection(savedSelection);
                                                        }
                                                        
                                                        // Remove link
                                                        editor.chain().focus().unsetLink().run();
                                                        
                                                        // Clear input
                                                        setLinkUrl('');
                                                        
                                                        // Save HTML
                                                        setTimeout(() => onChange(editor.getHTML()), 50);
                                                        
                                                        // Close section
                                                        setExpandedSection(null);
                                                        setSavedSelection(null);
                                                    }}
                                                >
                                                    <i className="fas fa-unlink"></i> Remove Link
                                                </button>
                                            )}
                                        </div>
                                        
                                        {/* Link Info */}
                                        <div className="tiptap-link-info">
                                            <i className="fas fa-info-circle"></i>
                                            <span>Opens in new tab</span>
                                        </div>
                                    </div>
                                    
                                    <span className="section-label">Link</span>
                                </div>
                            );
                        })()}

                        {/* 🎨 EFFECTS SECTION - Combined Text Box + Animation */}
                        {expandedSection === 'effects' && (onTextBoxChange || onAnimationChange) && (
                            <div className="tiptap-expanded-section tiptap-effects-section">
                                {/* TEXT BOX SUB-SECTION */}
                                {onTextBoxChange && (
                                    <div className="tiptap-effects-subsection">
                                        <div className="button-effect-header" style={{ marginBottom: '12px' }}>
                                            <label className="btn-custom-toggle-wrapper">
                                                <input
                                                    type="checkbox"
                                                    checked={textBoxEnabled || false}
                                                    onChange={(e) => {
                                                        const enabled = e.target.checked;
                                                        onTextBoxChange({ textBoxEnabled: enabled });
                                                    }}
                                                    className="btn-custom-toggle-input"
                                                />
                                                <span className="btn-custom-toggle-slider"></span>
                                                <span className="btn-custom-toggle-label">Text Box</span>
                                            </label>
                                        </div>

                                        {textBoxEnabled && (
                                            <>
                                                {/* Background Color */}
                                                <div className="button-color-picker-row">
                                                    <label className="button-slider-label">Background</label>
                                                    <input
                                                        type="color"
                                                        value={textBoxBackgroundColor || '#000000'}
                                                        onChange={(e) => {
                                                            onTextBoxChange({ textBoxBackgroundColor: e.target.value });
                                                        }}
                                                        className="button-color-picker"
                                                    />
                                                    <span className="button-slider-value" style={{ fontSize: '9px', textTransform: 'uppercase' }}>
                                                        {textBoxBackgroundColor || '#000'}
                                                    </span>
                                                </div>

                                                {/* Padding Horizontal */}
                                                <div className="button-slider-control">
                                                    <label className="button-slider-label">Padding H</label>
                                                    <input
                                                        type="range"
                                                        min="4"
                                                        max="40"
                                                        step="2"
                                                        value={textBoxPaddingHorizontal || 12}
                                                        onChange={(e) => {
                                                            const val = parseInt(e.target.value);
                                                            onTextBoxChange({ textBoxPaddingHorizontal: val });
                                                        }}
                                                        className="button-slider"
                                                    />
                                                    <span className="button-slider-value">{textBoxPaddingHorizontal || 12}px</span>
                                                </div>

                                                {/* Padding Vertical */}
                                                <div className="button-slider-control">
                                                    <label className="button-slider-label">Padding V</label>
                                                    <input
                                                        type="range"
                                                        min="2"
                                                        max="30"
                                                        step="2"
                                                        value={textBoxPaddingVertical || 6}
                                                        onChange={(e) => {
                                                            const val = parseInt(e.target.value);
                                                            onTextBoxChange({ textBoxPaddingVertical: val });
                                                        }}
                                                        className="button-slider"
                                                    />
                                                    <span className="button-slider-value">{textBoxPaddingVertical || 6}px</span>
                                                </div>

                                                {/* Border Toggle */}
                                                <div className="button-effect-header" style={{ marginTop: '8px' }}>
                                                    <label className="btn-custom-toggle-wrapper btn-custom-toggle-small">
                                                        <input
                                                            type="checkbox"
                                                            checked={textBoxBorderEnabled || false}
                                                            onChange={(e) => {
                                                                const enabled = e.target.checked;
                                                                onTextBoxChange({ textBoxBorderEnabled: enabled });
                                                            }}
                                                            className="btn-custom-toggle-input"
                                                        />
                                                        <span className="btn-custom-toggle-slider"></span>
                                                        <span className="btn-custom-toggle-label">Border</span>
                                                    </label>
                                                </div>

                                                {textBoxBorderEnabled && (
                                                    <>
                                                        {/* Border Width */}
                                                        <div className="button-slider-control">
                                                            <label className="button-slider-label">Width</label>
                                                            <input
                                                                type="range"
                                                                min="1"
                                                                max="10"
                                                                step="1"
                                                                value={textBoxBorderWidth || 2}
                                                                onChange={(e) => {
                                                                    const val = parseInt(e.target.value);
                                                                    onTextBoxChange({ textBoxBorderWidth: val });
                                                                }}
                                                                className="button-slider"
                                                            />
                                                            <span className="button-slider-value">{textBoxBorderWidth || 2}px</span>
                                                        </div>

                                                        {/* Border Color */}
                                                        <div className="button-color-picker-row">
                                                            <label className="button-slider-label">Color</label>
                                                            <input
                                                                type="color"
                                                                value={textBoxBorderColor || '#ffffff'}
                                                                onChange={(e) => {
                                                                    onTextBoxChange({ textBoxBorderColor: e.target.value });
                                                                }}
                                                                className="button-color-picker"
                                                            />
                                                            <span className="button-slider-value" style={{ fontSize: '9px', textTransform: 'uppercase' }}>
                                                                {textBoxBorderColor || '#fff'}
                                                            </span>
                                                        </div>
                                                    </>
                                                )}

                                                {/* Border Radius */}
                                                <div className="button-slider-control">
                                                    <label className="button-slider-label">Radius</label>
                                                    <input
                                                        type="range"
                                                        min="0"
                                                        max="50"
                                                        step="1"
                                                        value={textBoxBorderRadius || 8}
                                                        onChange={(e) => {
                                                            const val = parseInt(e.target.value);
                                                            onTextBoxChange({ textBoxBorderRadius: val });
                                                        }}
                                                        className="button-slider"
                                                    />
                                                    <span className="button-slider-value">{textBoxBorderRadius || 8}px</span>
                                                </div>
                                            </>
                                        )}
                                    </div>
                                )}

                                {/* Divider between Text Box and Animation */}
                                {onTextBoxChange && onAnimationChange && (
                                    <div className="button-effect-divider" style={{ margin: '16px 0' }} />
                                )}

                                {/* ANIMATION SUB-SECTION */}
                                {onAnimationChange && (
                                    <div className="tiptap-effects-subsection">
                                        <div className="button-effect-header" style={{ marginBottom: '12px' }}>
                                            <label className="btn-custom-toggle-wrapper">
                                                <input
                                                    type="checkbox"
                                                    checked={animationEnabled || false}
                                                    onChange={(e) => {
                                                        const enabled = e.target.checked;
                                                        onAnimationChange({ 
                                                            animationEnabled: enabled,
                                                            triggerAnimation: enabled ? Date.now() : null 
                                                        });
                                                        if (enabled) {
                                                            setAnimationKey(prev => prev + 1);
                                                        }
                                                    }}
                                                    className="btn-custom-toggle-input"
                                                />
                                                <span className="btn-custom-toggle-slider"></span>
                                                <span className="btn-custom-toggle-label">Animation</span>
                                            </label>
                                        </div>

                                        {animationEnabled && (
                                            <>
                                                {/* Animation Type Selection */}
                                                <div className="tiptap-animation-types">
                                                    <span className="button-slider-label">Type</span>
                                                    <div className="tiptap-animation-grid">
                                                        <button
                                                            type="button"
                                                            className={`tiptap-text-animation-selector ${animationType === 'fadeInUp' ? 'active' : ''}`}
                                                            onClick={(e) => {
                                                                e.preventDefault();
                                                                e.stopPropagation();
                                                                onAnimationChange({ 
                                                                    animationType: 'fadeInUp',
                                                                    triggerAnimation: Date.now()
                                                                });
                                                            }}
                                                            title="Fade In Up"
                                                        >
                                                            <div className="tiptap-animation-icon">↗️</div>
                                                            <span>Fade Up</span>
                                                        </button>

                                                        <button
                                                            type="button"
                                                            className={`tiptap-text-animation-selector ${animationType === 'slideInLeft' ? 'active' : ''}`}
                                                            onClick={(e) => {
                                                                e.preventDefault();
                                                                e.stopPropagation();
                                                                onAnimationChange({ 
                                                                    animationType: 'slideInLeft',
                                                                    triggerAnimation: Date.now()
                                                                });
                                                            }}
                                                            title="Slide In Left"
                                                        >
                                                            <div className="tiptap-animation-icon">➡️</div>
                                                            <span>Slide</span>
                                                        </button>

                                                        <button
                                                            type="button"
                                                            className={`tiptap-text-animation-selector ${animationType === 'fadeIn' ? 'active' : ''}`}
                                                            onClick={(e) => {
                                                                e.preventDefault();
                                                                e.stopPropagation();
                                                                onAnimationChange({ 
                                                                    animationType: 'fadeIn',
                                                                    triggerAnimation: Date.now()
                                                                });
                                                            }}
                                                            title="Fade In"
                                                        >
                                                            <div className="tiptap-animation-icon">✨</div>
                                                            <span>Fade</span>
                                                        </button>

                                                        <button
                                                            type="button"
                                                            className={`tiptap-text-animation-selector ${animationType === 'elegantScale' ? 'active' : ''}`}
                                                            onClick={(e) => {
                                                                e.preventDefault();
                                                                e.stopPropagation();
                                                                onAnimationChange({ 
                                                                    animationType: 'elegantScale',
                                                                    triggerAnimation: Date.now()
                                                                });
                                                            }}
                                                            title="Elegant Scale - Premium smooth animation"
                                                        >
                                                            <div className="tiptap-animation-icon">✨</div>
                                                            <span>Elegant</span>
                                                        </button>
                                                    </div>
                                                </div>

                                                {/* Animation Speed */}
                                                <div className="tiptap-animation-speed">
                                                    <span className="button-slider-label">Speed</span>
                                                    <div className="tiptap-speed-buttons">
                                                        <button
                                                            type="button"
                                                            className={`tiptap-text-speed-selector ${animationSpeed === 'fast' ? 'active' : ''}`}
                                                            onClick={(e) => {
                                                                e.preventDefault();
                                                                e.stopPropagation();
                                                                onAnimationChange({ 
                                                                    animationSpeed: 'fast',
                                                                    triggerAnimation: Date.now()
                                                                });
                                                            }}
                                                        >
                                                            Fast
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className={`tiptap-text-speed-selector ${animationSpeed === 'normal' ? 'active' : ''}`}
                                                            onClick={(e) => {
                                                                e.preventDefault();
                                                                e.stopPropagation();
                                                                onAnimationChange({ 
                                                                    animationSpeed: 'normal',
                                                                    triggerAnimation: Date.now()
                                                                });
                                                            }}
                                                        >
                                                            Normal
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className={`tiptap-text-speed-selector ${animationSpeed === 'slow' ? 'active' : ''}`}
                                                            onClick={(e) => {
                                                                e.preventDefault();
                                                                e.stopPropagation();
                                                                onAnimationChange({ 
                                                                    animationSpeed: 'slow',
                                                                    triggerAnimation: Date.now()
                                                                });
                                                            }}
                                                        >
                                                            Slow
                                                        </button>
                                                    </div>
                                                </div>

                                                {/* Preview Button */}
                                                <button
                                                    type="button"
                                                    className="tiptap-text-animation-preview"
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                        onAnimationChange({ triggerAnimation: Date.now() });
                                                    }}
                                                >
                                                    <i className="fas fa-play"></i> Preview
                                                </button>
                                            </>
                                        )}
                                    </div>
                                )}
                                
                                <span className="section-label">EFFECTS</span>
                            </div>
                        )}

                        {/* 🎬 OLD ANIMATION SECTION - REMOVED (now in Effects section) */}
                        {false && expandedSection === 'animation' && onAnimationChange && (
                            <div className="tiptap-expanded-section tiptap-animation-section">
                                {/* Animation Toggle */}
                                <div className="button-effect-header">
                                    <label className="btn-custom-toggle-wrapper">
                                        <input
                                            type="checkbox"
                                            checked={animationEnabled || false}
                                            onChange={(e) => {
                                                const enabled = e.target.checked;
                                                onAnimationChange({ 
                                                    animationEnabled: enabled,
                                                    triggerAnimation: enabled ? Date.now() : null 
                                                });
                                            }}
                                            className="btn-custom-toggle-input"
                                        />
                                        <span className="btn-custom-toggle-slider"></span>
                                        <span className="btn-custom-toggle-label">Animation</span>
                                    </label>
                                </div>

                                {animationEnabled && (
                                    <>
                                        {/* Animation Type Selection */}
                                        <div className="tiptap-animation-types">
                                            <span className="button-slider-label">Animation Type</span>
                                            <div className="tiptap-animation-grid">
                                                {/* Fade In Up */}
                                                <button
                                                    type="button"
                                                    className={`tiptap-animation-btn ${animationType === 'fadeInUp' ? 'active' : ''}`}
                                                    onClick={() => {
                                                        onAnimationChange({ 
                                                            animationType: 'fadeInUp',
                                                            triggerAnimation: Date.now()
                                                        });
                                                        setAnimationKey(prev => prev + 1);
                                                    }}
                                                    title="Fade In Up"
                                                >
                                                    <div className="animation-preview-icon">↗️</div>
                                                    <span>Fade Up</span>
                                                </button>

                                                {/* Slide In Left */}
                                                <button
                                                    type="button"
                                                    className={`tiptap-animation-btn ${animationType === 'slideInLeft' ? 'active' : ''}`}
                                                    onClick={() => {
                                                        onAnimationChange({ 
                                                            animationType: 'slideInLeft',
                                                            triggerAnimation: Date.now()
                                                        });
                                                        setAnimationKey(prev => prev + 1);
                                                    }}
                                                    title="Slide In Left"
                                                >
                                                    <div className="animation-preview-icon">➡️</div>
                                                    <span>Slide</span>
                                                </button>

                                                {/* Fade In */}
                                                <button
                                                    type="button"
                                                    className={`tiptap-animation-btn ${animationType === 'fadeIn' ? 'active' : ''}`}
                                                    onClick={() => {
                                                        onAnimationChange({ 
                                                            animationType: 'fadeIn',
                                                            triggerAnimation: Date.now()
                                                        });
                                                        setAnimationKey(prev => prev + 1);
                                                    }}
                                                    title="Fade In"
                                                >
                                                    <div className="animation-preview-icon">✨</div>
                                                    <span>Fade</span>
                                                </button>

                                                {/* Elegant Scale */}
                                                <button
                                                    type="button"
                                                    className={`tiptap-animation-btn ${animationType === 'elegantScale' ? 'active' : ''}`}
                                                    onClick={() => {
                                                        onAnimationChange({ 
                                                            animationType: 'elegantScale',
                                                            triggerAnimation: Date.now()
                                                        });
                                                        setAnimationKey(prev => prev + 1);
                                                    }}
                                                    title="Elegant Scale - Premium smooth animation"
                                                >
                                                    <div className="animation-preview-icon">✨</div>
                                                    <span>Elegant</span>
                                                </button>
                                            </div>
                                        </div>

                                        {/* Animation Speed */}
                                        <div className="tiptap-animation-speed">
                                            <span className="button-slider-label">Speed</span>
                                            <div className="tiptap-speed-buttons">
                                                <button
                                                    type="button"
                                                    className={`tiptap-speed-btn ${animationSpeed === 'fast' ? 'active' : ''}`}
                                                    onClick={() => {
                                                        onAnimationChange({ 
                                                            animationSpeed: 'fast',
                                                            triggerAnimation: Date.now()
                                                        });
                                                        setAnimationKey(prev => prev + 1);
                                                    }}
                                                >
                                                    Fast
                                                </button>
                                                <button
                                                    type="button"
                                                    className={`tiptap-speed-btn ${animationSpeed === 'normal' ? 'active' : ''}`}
                                                    onClick={() => {
                                                        onAnimationChange({ 
                                                            animationSpeed: 'normal',
                                                            triggerAnimation: Date.now()
                                                        });
                                                        setAnimationKey(prev => prev + 1);
                                                    }}
                                                >
                                                    Normal
                                                </button>
                                                <button
                                                    type="button"
                                                    className={`tiptap-speed-btn ${animationSpeed === 'slow' ? 'active' : ''}`}
                                                    onClick={() => {
                                                        onAnimationChange({ 
                                                            animationSpeed: 'slow',
                                                            triggerAnimation: Date.now()
                                                        });
                                                        setAnimationKey(prev => prev + 1);
                                                    }}
                                                >
                                                    Slow
                                                </button>
                                            </div>
                                        </div>

                                        {/* Preview Button */}
                                        <button
                                            type="button"
                                            className="tiptap-animation-preview-btn"
                                            onClick={() => {
                                                onAnimationChange({ triggerAnimation: Date.now() });
                                                setAnimationKey(prev => prev + 1);
                                            }}
                                        >
                                            <i className="fas fa-play"></i> Preview Animation
                                        </button>

                                                        {/* Info */}
                                                        <div className="tiptap-animation-info">
                                                            <i className="fas fa-info-circle"></i>
                                                            <span>Animation plays automatically when user scrolls to element (once per page visit)</span>
                                                        </div>
                                    </>
                                )}
                                
                                <span className="section-label">ANIMATION</span>
                            </div>
                        )}
                    </div>
                </div>,
            document.body
        );
    };

    return (
        <div 
            ref={wrapperRef} 
            className={wrapperClasses} 
            style={wrapperStyle}
            data-animation-key={animationKey}
        >
            {/* 🎨 TOOLBAR - wird über Portal in Body gerendert */}
            {renderToolbar()}

            {/* TipTap Editor Content */}
            <EditorContent editor={editor} />
        </div>
    );
}

export default TipTapInlineEditor;

